import { useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ListChecks, Loader2, Play, ShieldAlert, Trash2, X } from 'lucide-react';
import type {
  BulkActionId,
  BulkActionRequest,
  BulkActionRisk,
  BulkLimits,
  BulkOnuFilter,
  BulkPlan,
  BulkPlanSummary,
  Olt,
  OltVendor,
  OltVlan,
  OltVlanListResponse,
  Page,
  PonPort,
  Topology,
  Zone,
} from '@simpleacs/shared';
import {
  bulkActionIdSchema,
  BULK_ACTION_RISK,
  BULK_LIMIT_ALL,
  BULK_LIMIT_NONE,
  BULK_MAX_TARGETS_OPTIONS,
  BULK_MAX_TARGETS_PER_OLT_OPTIONS,
  BULK_SSID_WPA_MODE_LABELS,
  IPV6_ADDRESS_MODE_LABELS,
  IPV6_ADDRESS_MODES,
  IPV6_PREFIX_MODE_LABELS,
  IPV6_PREFIX_MODES,
  IPV6_REFUSAL,
  SSID_BANDS,
  bulkConfirmation,
  oltFamily,
  onuStateSchema,
  NEXT_FREE_SSID_PORT,
  vlanLabel as vlanIdAndName,
  primarySsidPort,
  ssidIndexOf,
  ssidPortsForBand,
  offerVlansToOnu,
  type BulkSsidWpaMode,
  type Ipv6AddressMode,
  type Ipv6PrefixMode,
  type SsidBand,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import {
  defaultWanModeRequest,
  ipv6ModesOf,
  wanModeReady,
  wanModeRequestToSend,
  withOltVendor,
  withWanAddressing,
  withWanMode,
  type BulkWanModeRequest,
} from '../lib/bulk-wan-mode';
import { markSettled, nextRunningId, runHasSettled } from '../lib/bulk-run-state';
import { withBands } from '../lib/bulk-ssid';
import { formatRelative, formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import { ZoneSelect } from '../components/topology-select';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/**
 * Bulk actions on ONUs.
 *
 * The screen deliberately has three steps and refuses to collapse them into
 * one, because the collapse is the accident: filter → PLAN → confirm the count
 * → run. The plan is a server object with the matched ONUs frozen into it, so
 * what runs is what was read, even if the fleet moves in between.
 *
 * Nothing here can get past the size cap. The cap is enforced when the plan is
 * executed, on the server, over the rows the server itself froze — this page
 * shows it so an operator is not surprised, and that is all it does. A limit in
 * a browser is not a limit.
 *
 * ONE CHASSIS per run, which is the operator's rule and also what makes this
 * form answerable: with an OLT chosen, "which VLANs" and "which PON ports"
 * have real answers and become dropdowns. Free-typing them was the bug — an
 * operator could enter a VLAN the chassis does not carry or a port that does
 * not exist, and only find out when the plan came back refused. The dropdowns
 * are a convenience; every one of them is still checked server-side, because a
 * caller can skip the form entirely.
 */

const ACTION_HELP: Record<string, string> = {
  device_preset:
    'What the ONU itself is told — SSID, key, radios, WAN and LAN. Resolved against each CPE’s own reported paths, one CPE at a time.',
  management_mode: 'TR-069 or OMCI, and the management VLAN the ONU reaches the ACS on.',
  tr069_profile: 'Which ACS deployment the ONU is pointed at. Written to the chassis at its next management change.',
  attach_vlan: 'Adds a subscriber VLAN and pushes the service config. No LAN socket is put on it.',
  // Not offered in the picker since 2026-09-14 — `wan_mode` carries everything
  // it did, and the `?action=vlan_mode` link is translated to it on mount. The
  // id is still in the request union for the ledger's runs, so the branches
  // that render it stay complete rather than fall through.
  vlan_mode: 'Bridged (the ONU passes it on) or routed (the ONU terminates it). The connection renegotiates.',
  wan_mode:
    'The WAN dialog’s Apply, for every ONU in the run — routed or bridged, how a routed ONU gets its address (from each ONU’s own record), IPv4 only or dual stack, and the firewall. The connection renegotiates.',
  vlan_change:
    'Moves subscribers off one VLAN and onto another. An ONU that is not on the old VLAN is left alone and says so — it is never attached instead.',
  ssid_set:
    'Writes an SSID name and key onto the WiFi you choose, per band. A CPE that has informed is written on the CPE over TR-069 — where the fleet’s own names, spaces and all, are ordinary — and an ONU that never has is written down the chassis over OMCI, where the name must be ONE word and only a ZTE chassis has the command. Each row says which way it went. A band the ONU’s model has no radio for is refused by name on that ONU and never moved to the other radio.',
  ssid_remove:
    'Turns off the WiFi you choose — WiFi 1–4 on 2.4 GHz, WiFi 5–8 on 5 GHz — through the ACS. Each ONU’s row names the network that interface is carrying today, because a slot holds whatever that subscriber has in it. It is a disable, not a delete: the network stops being offered and its key stays on the CPE. Needs the ONU to have informed an ACS — over OMCI no chassis here has a command for this.',
  hotspot:
    'A public hotspot on ZTE OMCI-managed ONUs: a bridged VLAN, the second SSID interface on each radio bound to it, and the public name and key on both — one chassis session per ONU. Turning it off hides both SSIDs; detaching the VLAN waits on the first supervised VLAN removal on this fleet and says so on every row. ZTE chassis only — a Huawei OLT has no ONT wifi command.',
  zone: 'Placement only. No chassis and no CPE is written.',
  firmware_campaign:
    'Creates a staged campaign — batches, canary first, pause on failure. Creating it does not start it. An image that does not change the version the CPE reports confirms by reboot without fault: the device rebooted after the push, informed after that boot, and GenieACS holds no fault for it since.',
  onu_resync:
    'Writes SimpleACS’s stored VLANs and ports over whatever the chassis has now, then finishes on each TR-069 router: its stored WiFi re-sent and its recorded WAN re-applied over TR-069 even where the router already carries it — never re-roling a working connection, moving it to another VLAN, or turning PPPoE or a static address into DHCP. The ONU is not deauthorised; a router’s WAN may reconnect for a moment as it is re-applied. With “reboot first” each ONU is rebooted and its config written while it is off the PON — how a word an ONT declines while up gets stored on a ZTE chassis.',
  onu_adopt:
    'Reads each ONU’s configuration off the chassis — and, where one is linked, off its router — and records what is missing here: VLANs, sockets, the ACS it answers to, the config snapshot its tabs read, and the router’s hotspot (an SSID bridged to a VLAN) and open WiFi networks, so a resync or a reset can put them back. A router-default “WirelessNet…” network is never recorded — the row names it so it can be deleted. Nothing is written to any chassis or CPE, and a row already recorded is never touched: running it twice records nothing twice.',
  onu_summon:
    'Wakes each ONU’s CPE over TR-069 — a connection request plus a refresh of the common parameters: WiFi states, WAN, addresses. Nothing is configured anywhere. A CPE that cannot be reached runs the refresh at its next inform, and an ONU that has never informed an ACS is refused by name.',
  onu_reboot:
    'Reboots each ONU through the chassis. Every one of them loses service for about a minute while it re-ranges. Configuration is untouched.',
  wan_ipv6:
    'The subscriber WAN’s IP protocol and nothing else — dual stack with its two modes, or IPv4 only. The VLAN, the routed/bridged role, the addressing and the PPPoE account are left exactly as they are, which is what makes this runnable across a fleet. Judged per ONU by the same gate as the WAN dialog: a CPE with no observed word for the mode chosen is listed on its own row with that sentence and nothing is written to it. Delivered over TR-069 where the CPE is linked; recorded on the subscriber’s routed VLAN and delivered at first link where it is not.',
  router_clock:
    'Puts each router’s own clock on East Africa Time and switches NTP on, using this organization’s time servers (Settings → NTP), in each router family’s own spelling. Only what differs is written, and a router already on EAT and synchronised is left alone. No connection, VLAN or address is touched. A router that is down or silent gets it at its next inform.',
  onu_firewall:
    'Whether each ONU answers unsolicited traffic from the WAN side — the whole-ONU OMCI flag, through the chassis. ZTE only: a Huawei OLT has no ONT firewall verb, and its ONTs take this over TR-069.',
  onu_recreate:
    'Deauthorises each ONU and builds it again from what SimpleACS holds, then re-sends each TR-069 router’s stored WiFi and re-applies its recorded WAN where it is missing or differs — never re-roling a working connection, moving it to another VLAN, or turning PPPoE or a static address into DHCP. An outage plus a re-provision, per subscriber — not a change another write undoes.',
  profile_migrate:
    'Moves every rate binding each ONU carries — data and management T-CONTs and GEMs — off the named profiles onto ones the chassis already holds, by name, and reads each one back. It never writes a profile. ZTE C300 so far.',
  speed_rebind:
    'Binds each subscriber to a speed profile the chassis already holds. It never writes a profile: one edit of a shared name re-rates every subscriber on it, so a speed nothing on this chassis carries is refused rather than created. On Huawei the service-port is deleted and recreated at the same index, and the subscriber is down for the gap.',
};

/**
 * How each rung reads on screen.
 *
 * The operator's rule, made literal: the thing in front of 2,000 reboots must
 * not look like the thing in front of 2,000 resyncs. Colour and wording move
 * with `BULK_ACTION_RISK`, which is the same table the server refuses from, so
 * the two can never describe a run differently.
 */
/**
 * One speed profile as the chassis reports it.
 *
 * `bidirectional` matters to the pickers rather than to the display: a Huawei
 * traffic table and a C650 `traffic-profile` carry no direction of their own —
 * the direction comes from where they are attached, and index 7 is bound
 * inbound AND outbound on 1,949 management service-ports — so such a profile
 * belongs in both lists. A ZTE T-CONT profile and a ZTE traffic profile are
 * genuinely different object types and belong in one each.
 */
interface SpeedProfileOption {
  name: string;
  direction: 'up' | 'down';
  maxKbps: number | null;
  boundCount: number | null;
  bidirectional?: boolean;
}

/** kbps as an operator says it, the same way the OLT's own tab says it. */
function speedLabel(kbps: number | null): string {
  if (kbps === null || kbps <= 0) return 'rate not stated';
  if (kbps >= 1_000_000) {
    const gig = kbps / 1_048_576;
    return `${gig >= 0.95 && gig <= 1.05 ? 1 : Math.round(gig * 10) / 10}G`;
  }
  return `${Math.round(kbps / 1024)}M`;
}

/**
 * `SMARTOLT-1G-DOWN · 1G · 2,120 bound` — the name the chassis uses, what it
 * is worth, and how many subscribers are already on it.
 *
 * The bound count is the number that stops the accident this whole feature is
 * shaped around. It is not a warning about the REBIND — moving somebody onto a
 * shared profile is the normal case and harms nobody — it is the number an
 * operator needs in front of them so that "why not just edit the profile" never
 * looks like a shortcut.
 */
function speedProfileLabel(p: SpeedProfileOption): string {
  const parts = [p.name, speedLabel(p.maxKbps)];
  if (p.boundCount !== null && p.boundCount !== undefined) {
    parts.push(`${p.boundCount.toLocaleString()} bound`);
  }
  return parts.join(' · ');
}

const RISK_NOTE: Record<BulkActionRisk, { tone: string; what: string }> = {
  read: { tone: 'text-muted', what: 'Reads only — nothing is written.' },
  write: {
    tone: 'text-muted',
    what: 'A configuration change. A wrong one is put right by another run.',
  },
  service: {
    tone: 'text-warn',
    what: 'Service-affecting: every subscriber in this run loses their connection while it happens.',
  },
  rebuild: {
    tone: 'text-danger',
    what: 'Each ONU is taken off the chassis and rebuilt. On the wrong selection that is an outage plus a re-provision, per subscriber.',
  },
};

/**
 * `oltId` is `''` until the operator picks one.
 *
 * The schema requires a real id, so an empty string cannot be planned — which
 * is the point: there is no fleet-wide bulk run to accidentally build. The
 * Build button stays disabled and the dependent dropdowns have nothing to load
 * until a chassis is chosen.
 */
const EMPTY_FILTER: BulkOnuFilter = {
  oltId: '',
  ponPort: null,
  state: null,
  vlan: null,
  wanMode: null,
  zoneId: null,
  splitterId: null,
  onuType: null,
  managementMode: null,
  managementState: null,
  hasDevice: null,
  search: null,
  nameStartsWith: null,
  nameNotStartsWith: null,
  onuIds: null,
};

interface PreviewResult {
  olt: { id: string; name: string; vendor: string };
  count: number;
  /** The spread across that chassis's PON ports — 30 on one port is a splitter. */
  perPonPort: Array<{ ponPort: string; count: number }>;
  limits: BulkLimits;
  withinCap: boolean;
  capReason: string | null;
}

export function BulkActionsPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const editable = can('bulk.execute');

  /**
   * A selection handed over from the ONU list.
   *
   * IDS, not the list's filter predicates. Translating one filter language
   * into the other would be the dangerous kind of nearly-right: the ONU list
   * can narrow by signal, by service state and by TR-069 reachability, and a
   * plan built from the subset that does translate would target MORE
   * subscribers than the operator was looking at when they pressed the button.
   * The set that was on screen is the set that runs, which is also why the
   * sender refuses to hand over more than the schema's 500.
   */
  const handoff = (useLocation().state ?? null) as {
    oltId?: string;
    onuIds?: string[];
    /** An action to pre-select — e.g. the drift board hands over `onu_resync`. */
    action?: BulkActionId;
  } | null;

  const [filter, setFilter] = useState<BulkOnuFilter>(() =>
    handoff?.oltId && handoff.onuIds?.length
      ? { ...EMPTY_FILTER, oltId: handoff.oltId, onuIds: handoff.onuIds }
      : EMPTY_FILTER,
  );
  const [request, setRequest] = useState<BulkActionRequest>(() =>
    handoff?.action ? defaultRequest(handoff.action) : { action: 'zone', zoneId: null },
  );
  const [plan, setPlan] = useState<BulkPlan | null>(null);
  /**
   * The confirmation is a dialog now, not three boxes to type into.
   *
   * Typing the count, the chassis name and a word in capitals was real
   * friction, and friction is not the same thing as attention: an operator who
   * has typed REBOOT nine times types it the tenth without reading the number
   * above it. What the dialog does instead is put the count, the chassis and
   * what the action costs the subscriber in front of a deliberate second click,
   * on a surface that cannot be dismissed by pressing Enter out of habit.
   *
   * The server contract is unchanged — it still receives and checks all three
   * confirmations, and still refuses a mismatch. They are now filled from the
   * plan the dialog is describing rather than from the keyboard, so the check
   * remains a client/server agreement about WHICH plan is being run.
   */
  /**
   * Send the settings even where this platform believes they are already there.
   *
   * The four "already …" preconditions read SimpleACS's own record, not the
   * chassis, and a record can drift — which on this fleet it has. This turns
   * those four into warnings on the row so the write goes anyway; nothing the
   * CHASSIS or the hardware refuses is affected.
   */
  const [force, setForce] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /**
   * The plan being executed right now, so its progress can be polled.
   *
   * `/execute` does not answer until the last ONU is done — ten minutes, for a
   * chassis-wide management change — so without this the screen shows a spinner
   * and no way to tell a run that is working from one that has stalled. The
   * per-target rows are written as each ONU finishes and the plan's counters are
   * derived from them on read, so re-reading the plan on a timer is a true
   * progress report and costs the executor nothing.
   */
  const [runningId, setRunningId] = useState<string | null>(null);
  /**
   * Every plan this page has watched to the end, for as long as it is mounted.
   *
   * A ref and not state, because it is a memory the effects consult and never
   * something the screen shows — and because the loop it exists to stop was
   * made of state updates. On 2026-09-13 a one-ONU resync finished inside a
   * second: the live plan said `executed` while the history list, fetched
   * right after the 202, still said `running`. Settle cleared `runningId`,
   * re-attach read the stale list and put the same id back, the live query's
   * cached answer settled it again, and so on — 28 requests in one second and
   * a white page. A plan in this set is never adopted again, whatever the
   * list says; see `nextRunningId`.
   */
  const settledRuns = useRef<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const resetConfirm = () => setConfirmOpen(false);

  const olts = useQuery({
    queryKey: ['olts', 'bulk'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
  });
  // Zones come off the topology read — there is no flat zone list endpoint,
  // and adding one for a dropdown would be a second source for the same rows.
  const topology = useQuery({
    queryKey: ['topology', 'bulk'],
    queryFn: () => api.get<Topology>('/topology'),
  });
  /**
   * The profiles the CHOSEN CHASSIS is associated with, not the org's whole
   * list. Anything else is refused at the plan anyway, so offering it was
   * offering a refusal — and the operator's rule is that a picker shows the
   * actual profiles by name, the default marked, never a sentence about
   * inheritance. Same query key as the ONU page's Management dialog, so the
   * two share a cache entry.
   */
  const oltProfiles = useQuery({
    queryKey: ['olt', filter.oltId, 'tr069-profiles'],
    queryFn: () =>
      api.get<{ items: Array<{ profileId: string; name: string; isDefault: boolean }> }>(
        `/olts/${encodeURIComponent(filter.oltId)}/tr069-profiles`,
      ),
    enabled: Boolean(filter.oltId),
  });
  /**
   * The speed profiles the CHOSEN CHASSIS holds, for the rebind's two pickers.
   *
   * Read from the OLT rather than from a list kept here, because a rebind may
   * only name a profile that already exists on that chassis — the server
   * refuses anything else with `profile-absent-from-chassis` — and a picker
   * offering names the chassis has not got is a form that builds plans nobody
   * can run. It is the same snapshotted read the OLT's own Speed profiles tab
   * uses, so it costs no chassis session on a warm cache.
   *
   * Only fetched for the one action that needs it: it is a live CLI read behind
   * the snapshot, and every other run on this page would be paying for it.
   */
  const speedProfiles = useQuery({
    queryKey: ['olt', filter.oltId, 'speed-profiles'],
    queryFn: () =>
      api.get<{
        data?: { items: SpeedProfileOption[] };
        items?: SpeedProfileOption[];
      }>(`/olts/${encodeURIComponent(filter.oltId)}/speed-profiles`),
    enabled: Boolean(filter.oltId) && request.action === 'speed_rebind',
  });
  const speedProfileItems =
    speedProfiles.data?.data?.items ?? speedProfiles.data?.items ?? [];

  const presets = useQuery({
    queryKey: ['device-presets', 'bulk'],
    queryFn: () => api.get<Page<{ id: string; name: string; target: string }>>('/device-presets'),
  });
  const history = useQuery({
    queryKey: ['bulk-plans'],
    queryFn: () => api.get<Page<BulkPlanSummary>>('/bulk-actions/plans', { limit: 25 }),
    // The operator watches this table during a run; it refreshes itself.
    refetchInterval: 15_000,
  });
  /**
   * What the server says it can do, and where it says it cannot.
   *
   * The action list itself is still hardcoded below — every option here has a
   * `case` in `defaultRequest` and `ActionFields`, so it could not be data
   * without inventing a form for an action nobody has written one for. What
   * this brings back is the part that genuinely varies per chassis: the vendor
   * gate.
   */
  const catalogue = useQuery({
    queryKey: ['bulk-actions-catalogue'],
    queryFn: () =>
      api.get<{ vendorGate: Record<string, Record<string, string | undefined>> }>(
        '/bulk-actions/actions',
      ),
  });

  /**
   * What the CHOSEN chassis carries. Both lists are per-OLT and neither is
   * fetched until one is picked — an empty dropdown reads as "this OLT has no
   * VLANs", which is a different and much worse statement than "choose an OLT".
   */
  const ponPortQuery = useQuery({
    queryKey: ['pon-ports', filter.oltId],
    queryFn: () => api.get<Page<PonPort>>(`/olts/${filter.oltId}/pon-ports`),
    enabled: Boolean(filter.oltId),
  });
  const onuTypeQuery = useQuery({
    queryKey: ['olt-onu-types', filter.oltId],
    queryFn: () =>
      api.get<Page<{ onuType: string; count: number }>>(
        `/olts/${encodeURIComponent(filter.oltId)}/onu-types`,
      ),
    enabled: Boolean(filter.oltId),
  });
  const onuTypeOptions = onuTypeQuery.data?.items ?? [];

  const vlanQuery = useQuery({
    queryKey: ['olt-vlans', filter.oltId],
    queryFn: () => api.get<OltVlanListResponse>(`/olts/${filter.oltId}/vlans`),
    enabled: Boolean(filter.oltId),
  });

  const ponPortOptions = ponPortQuery.data?.items ?? [];
  const vlanOptions = vlanQuery.data?.items ?? [];
  /**
   * Only the VLANs a subscriber may be put on, by the same rule the authorise
   * wizard's picker uses (`offerVlansToOnu`, shared with the server). A
   * management VLAN is the platform's own path to the ONU: attaching a
   * subscriber to it means their traffic and the chassis's are one, and the
   * first sign is usually that management stops working.
   *
   * No `alreadyOn`: a bulk run names a VLAN to MOVE a batch of ONUs onto, and
   * the batch is a filter rather than an ONU, so there is no current value for
   * any one subscriber to preserve. VLAN 1 appears here only where the tenant
   * has turned it on. The per-ONU dialogs are where an ONU already on VLAN 1
   * keeps being editable — see `WanModeDialog`.
   *
   * The FILTER box below is deliberately built from `vlanOptions` instead, and
   * offers every VLAN the chassis carries. Finding the subscribers on VLAN 1
   * is exactly the question an operator asks after turning it off; a filter
   * that hid them would hide the fleet rather than a choice.
   */
  const vlanOffer = offerVlansToOnu(vlanOptions, {
    vlan1Enabled: vlanQuery.data?.vlan1Selectable === true,
  });
  const subscriberVlans = vlanOffer.offered;
  /**
   * Where a move can start FROM, which is not the same list.
   *
   * A destination is a choice about where subscribers should be; a source is a
   * statement about where they already are, and the platform does not get to
   * disagree with that. Switching VLAN 1 off and then wanting the ONUs moved
   * off it is the obvious next act, and a "Move from" box that cannot name
   * VLAN 1 makes it the one move that cannot be done in bulk. The chassis's
   * management VLAN stays out — moving a subscriber off it is the Management
   * control's job, and `vlan_change` refuses it by name anyway.
   */
  const moveFromVlans = vlanOptions.filter((v) => !v.usedForMgmt);
  /**
   * And the other half of the same question, which this form did not ask.
   *
   * Management VLAN was a free-text number box here while every subscriber
   * VLAN on the page went through a picker. An operator typed 5 — a subscriber
   * VLAN on gachie1, where management is 10 — and 42 ONUs were moved to TR-069
   * over a VLAN they hold no address on. Same source as the ONU page's
   * Management dialog: only what this chassis is marked as managing on.
   */
  const managementVlans = vlanOptions.filter((v) => v.usedForMgmt);

  /**
   * The running count.
   *
   * Debounced rather than fired on every keystroke: the query walks the whole
   * inventory, and an operator typing a serial should not queue twelve of them.
   */
  const [debounced, setDebounced] = useState(filter);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(filter), 350);
    return () => clearTimeout(id);
  }, [filter]);

  const preview = useQuery({
    queryKey: ['bulk-preview', debounced],
    queryFn: () => api.post<PreviewResult>('/bulk-actions/preview', { filter: debounced }),
    enabled: Boolean(debounced.oltId),
  });

  const planMutation = useMutation({
    mutationFn: () =>
      api.post<BulkPlan>('/bulk-actions/plans', {
        filter,
        // The WAN-mode form's DNS box is free text; an empty one is sent as
        // null (the schema's "gateway-only profile"), never as "".
        request: request.action === 'wan_mode' ? wanModeRequestToSend(request) : request,
        force,
      }),
    onSuccess: (created) => {
      // The finished run leaves the banner; the remainder becomes the review.
      setRunningId(null);
      setPlan(created);
      resetConfirm();
      setError(null);
      setNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['bulk-plans'] });
    },
    onError: (err) => {
      setPlan(null);
      setError(err);
    },
  });

  const runMutation = useMutation({
    mutationFn: (planId: string) =>
      api.post<BulkPlan>(`/bulk-actions/plans/${planId}/execute`, {
        // From the plan the dialog just described, not from the keyboard. Still
        // sent whether or not this plan needs them: the server decides which it
        // requires, from the plan — a client that guesses wrong is refused, and
        // a client that sends more than was asked for is simply ignored.
        confirmCount: plan?.eligibleCount ?? -1,
        confirmOltName: planOltName,
        confirmPhrase: needed.phrase,
      }),
    onMutate: (planId: string) => {
      setConfirmOpen(false);
      setRunningId(planId);
    },
    /**
     * 202 means ACCEPTED, not finished.
     *
     * The endpoint answers as soon as the plan is claimed and the run is
     * certain to proceed; the run itself carries on server-side afterwards.
     * So this is the START of the progress bar's life, not the end of it —
     * what closes it is the plan's own status leaving `running`, in the effect
     * below. Which is also what lets an operator close the tab: the run does
     * not belong to their request any more.
     */
    onSuccess: (accepted) => {
      setPlan(accepted);
      setError(null);
      setNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['bulk-plans'] });
    },
    /**
     * A rejection here is a REFUSAL, not a lost run.
     *
     * Every check the server makes — the confirmations, the expiry, an empty
     * eligible set, a preset deleted since the plan was made — happens before
     * the plan is claimed. So if this rejects, nothing was written and nothing
     * is running, and the progress bar must go away rather than sit at 0%.
     */
    onError: (err) => {
      setRunningId(null);
      setError(err);
    },
  });

  /**
   * The remainder of a run that stopped, as a fresh plan.
   *
   * Deliberately lands the operator back on a DRAFT with the confirmations
   * empty rather than running anything: finishing a half-done job is still a
   * bulk write to live subscribers, and it gets the same review the first half
   * got.
   */
  const resumeMutation = useMutation({
    mutationFn: (planId: string) => api.post<BulkPlan>(`/bulk-actions/plans/${planId}/resume`, {}),
    onSuccess: (created) => {
      // The finished run leaves the banner; the remainder becomes the review.
      setRunningId(null);
      setPlan(created);
      resetConfirm();
      setError(null);
      setNotice(
        `Planned the ${created.eligibleCount} ONU${created.eligibleCount === 1 ? '' : 's'} the last run never reached. Review and confirm to finish them.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['bulk-plans'] });
    },
    onError: (err) => setError(err),
  });

  const discardMutation = useMutation({
    mutationFn: (planId: string) => api.delete<{ ok: boolean }>(`/bulk-actions/plans/${planId}`),
    onSuccess: () => {
      setPlan(null);
      resetConfirm();
      void queryClient.invalidateQueries({ queryKey: ['bulk-plans'] });
    },
  });

  const limits = preview.data?.limits ?? null;
  const ran = Boolean(plan?.executedAt);

  /**
   * What this PLAN asks for, from the plan itself.
   *
   * From `plan.action` and `plan.filter`, never from the form's current state:
   * an operator can change the action dropdown after building a plan, and the
   * plan is what runs. Both are frozen on the object the server returned.
   */
  const needed = plan
    ? bulkConfirmation(plan.action, plan.filter, plan.params as { reboot?: boolean })
    : { risk: 'write' as const, wholeChassis: false, oltName: false, phrase: null };
  const planOltName = plan?.oltName ?? plan?.oltId ?? 'this OLT';
  const stopRun = useMutation({
    mutationFn: (planId: string) =>
      api.post<{ ok: boolean }>(`/bulk-actions/plans/${encodeURIComponent(planId)}/stop`, {}),
    onSuccess: () => {
      setNotice('Stopping at the next ONU — finished targets keep their outcomes, and "Continue" picks up the rest.');
      void queryClient.invalidateQueries({ queryKey: ['bulk-plan-live'] });
    },
    onError: (err) => setError(err),
  });

  /**
   * The run in flight, re-read on a timer.
   *
   * A separate query key from `['bulk-plans']` so the history list is not
   * refetched every two seconds along with it, and `enabled` only while a run
   * is actually going — the moment the plan's own status leaves `running`,
   * the settle effect below clears `runningId` and this stops.
   */
  const live = useQuery({
    queryKey: ['bulk-plan-live', runningId],
    queryFn: () => api.get<BulkPlan>(`/bulk-actions/plans/${runningId}`),
    enabled: runningId !== null,
    refetchInterval: 2000,
  });

  /**
   * Progress, counted from the target rows the executor has already written.
   *
   * `skipped` counts toward done: a target the executor decided against is
   * finished with, not pending. `abandoned` deliberately does not — a run that
   * stopped has targets it will never reach, and showing them as done would put
   * the bar at 100% on a half-finished job.
   */
  const progress = (() => {
    const p = live.data;
    if (!runningId || !p) return null;
    const total = p.eligibleCount || 1;
    const done = p.succeeded + p.failed + p.skipped;
    return {
      done,
      total: p.eligibleCount,
      ok: p.succeeded,
      failed: p.failed,
      skipped: p.skipped,
      pct: Math.min(100, Math.round((done / total) * 100)),
    };
  })();

  /**
   * The run is over when the PLAN says so, which is the only thing that knows.
   *
   * Not when the request came back — that is now a 202 the moment the run is
   * accepted — and not when `done >= total`, which is true for a fraction of a
   * second before the last outcome is written and would close the bar early.
   *
   * And not on a `draft`. `onMutate` above sets `runningId` inside the click,
   * before the POST has left, so the live GET for this id goes out in the same
   * commit and — two reads against a claim that follows the target match, the
   * limits and the prepare — answers `draft` first, every time. The first cut
   * of `runHasSettled` read "not running" as "over", settled on that draft,
   * marked the id, and then refused the same id when the 202 said `running`:
   * the page's own run had no bar, no Stop and no finishing notice, for the
   * page's life. `runHasSettled` now answers only for `executed`, `failed`
   * and `expired`; a draft is a run that has not started.
   */
  useEffect(() => {
    const done = live.data;
    if (!runningId || !done || !runHasSettled(runningId, done)) return;
    /*
      Once per plan. The notice and the two invalidations belong to the first
      pass; a later pass for the same id — a cached `executed` answer arriving
      again — only makes sure the page has let go, and says nothing twice.
      Marking it here is also what the re-attach effect below reads, so the
      stale history row for this run can never put it back.
    */
    if (!markSettled(settledRuns.current, runningId)) {
      setRunningId(null);
      return;
    }
    setRunningId(null);
    setPlan(done);
    setNotice(
      `${done.succeeded} applied` +
        (done.failed > 0 ? `, ${done.failed} failed` : '') +
        (done.skipped > 0 ? `, ${done.skipped} skipped` : '') +
        (done.abandoned > 0 ? `, ${done.abandoned} never reached` : '') +
        '.',
    );
    void queryClient.invalidateQueries({ queryKey: ['bulk-plans'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [runningId, live.data, queryClient]);

  /**
   * Re-attach to a run that was already going when this page loaded.
   *
   * `runningId` used to be born only from this tab's own execute click, so
   * leaving the page orphaned a live forty-minute fleet operation — the bar
   * vanished while 190 subscribers were still being written to. The whole
   * point of the run outliving its request is that the operator can close the
   * tab; coming back to a screen with no sign of it would give that away
   * again. The history list already carries every plan's status, so a
   * `running` one is picked up without asking the server anything extra,
   * whoever started it.
   *
   * ONE effect, and it decides from more than the history row. There were two
   * identical copies of this, and each was blind to what the page already
   * knew: on 2026-09-13 a one-ONU resync finished before the history list
   * fetched at the 202 was replaced, so the list said `running` while the live
   * plan said `executed`. Settle let go, this took hold of the same id, the
   * cached live answer settled it again — 28 requests in one second, "Maximum
   * update depth exceeded", and a white page with no boundary to catch it.
   * `nextRunningId` refuses a plan this page has watched settle and a plan
   * whose cached live record is already past `running`; the list alone is
   * not evidence that a run is alive.
   */
  useEffect(() => {
    const next = nextRunningId({
      runningId,
      historyItems: history.data?.items,
      settled: settledRuns.current,
      cachedStatus: (id) => queryClient.getQueryData<BulkPlan>(['bulk-plan-live', id])?.status,
    });
    if (next !== runningId) setRunningId(next);
  }, [runningId, history.data, queryClient]);

  const patch = (over: Partial<BulkOnuFilter>) => {
    setFilter((f) => ({ ...f, ...over }));
    // The plan describes the OLD filter the moment the filter changes; keeping
    // it on screen would invite confirming a count that no longer belongs to
    // what is selected.
    setPlan(null);
    resetConfirm();
  };

  /**
   * Changing the chassis throws away everything that belonged to the old one.
   *
   * A VLAN id and a PON port only mean something on the OLT they were read
   * from — VLAN 20 exists on one chassis here and not on another. Carrying a
   * selection across would build a plan whose every target is refused, or
   * worse, one that looks fine because the number happens to exist on both.
   * The WAN-mode steps the vendor decides go the same way (`withOltVendor`):
   * a PPPoE/static addressing or an on/off firewall chosen on a ZTE chassis
   * has no control on a Huawei one, and a value the form cannot show is a
   * value it must not send.
   */
  const chooseOlt = (oltId: string) => {
    patch({ oltId, ponPort: null, vlan: null });
    const vendor = (olts.data?.items ?? []).find((o) => o.id === oltId)?.vendor ?? null;
    setRequest((r) => {
      const cleared = clearVlans(r);
      return cleared.action === 'wan_mode' ? withOltVendor(cleared, vendor) : cleared;
    });
  };

  /**
   * A deep link chooses the chassis and the action — the OLT page's Import
   * button arrives as /bulk?olt=<id>&action=onu_adopt. Read once on mount and
   * then cleared from the URL, so a reload does not re-impose a choice the
   * operator has since changed.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const olt = searchParams.get('olt');
    const wanted = searchParams.get('action');
    if (!olt && !wanted) return;
    if (olt) chooseOlt(olt);
    if (wanted) {
      const parsed = bulkActionIdSchema.safeParse(wanted);
      /**
       * `vlan_mode` left the picker on 2026-09-14 and its link lands on the
       * action that replaced it: `wan_mode` carries everything it did. Left
       * as it was, the select had no option for the state the form was in and
       * painted the first one ("Device config preset") over `vlan_mode`'s help,
       * fields and plan (review, 2026-09-14).
       */
      if (parsed.success) {
        setRequest(defaultRequest(parsed.data === 'vlan_mode' ? 'wan_mode' : parsed.data));
      }
    }
    setSearchParams({}, { replace: true });
    // Mount-only by design; the params are consumed and removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Every VLAN an action holds, unchosen.
   *
   * One helper rather than an `'vlanId' in r` test per call site, because
   * `vlan_change` holds TWO and a check that only knew about `vlanId` would
   * silently carry a stale "move from VLAN 20" across to a chassis that has no
   * VLAN 20 — the exact bug the OLT-change reset exists to prevent.
   */
  function clearVlans(r: BulkActionRequest): BulkActionRequest {
    if (r.action === 'vlan_change') return { ...r, fromVlanId: 0, toVlanId: 0 };
    /**
     * A rebind's unchosen VLAN is NULL, not 0, and the difference is a real
     * one: null means "the one subscriber VLAN SimpleACS records for this
     * ONU", which is a legitimate answer the server acts on, while 0 is below
     * the schema's minimum and would be rejected at the boundary.
     */
    if (r.action === 'speed_rebind') return { ...r, vlanId: null };
    /** The same word for the same reason: null is "each ONU's own subscriber VLAN". */
    if (r.action === 'wan_mode') return { ...r, vlanId: null };
    return 'vlanId' in r ? { ...r, vlanId: 0 } : r;
  }

  /**
   * Re-validate once the lists land.
   *
   * The clearing above handles the operator changing the OLT; this handles the
   * other direction — a VLAN removed from the chassis, or a port that vanished
   * from a re-sweep, while a selection referencing it was still on screen.
   */
  useEffect(() => {
    if (!filter.oltId || ponPortQuery.isPending) return;
    if (filter.ponPort && !ponPortOptions.some((p) => p.port === filter.ponPort)) {
      patch({ ponPort: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.oltId, filter.ponPort, ponPortQuery.isPending, ponPortOptions.length]);

  useEffect(() => {
    if (!filter.oltId || vlanQuery.isPending) return;
    if (filter.vlan !== null && !vlanOptions.some((v) => v.vlanId === filter.vlan)) {
      patch({ vlan: null });
    }
    const known = (id: number) => subscriberVlans.some((v) => v.vlanId === id);
    // The source is cleared against the wider list it is chosen from, or a VLAN
    // the operator legitimately picked to move OFF would be blanked the moment
    // this effect ran.
    const knownSource = (id: number) => moveFromVlans.some((v) => v.vlanId === id);
    setRequest((r) => {
      if (r.action === 'vlan_change') {
        return {
          ...r,
          fromVlanId: r.fromVlanId > 0 && !knownSource(r.fromVlanId) ? 0 : r.fromVlanId,
          toVlanId: r.toVlanId > 0 && !known(r.toVlanId) ? 0 : r.toVlanId,
        };
      }
      /** Back to "whichever the ONU records", not to an unchoosable 0. */
      if (r.action === 'ssid_set') {
        // Keep Access VLAN chosen; forget a VLAN this chassis does not carry.
        return r.vlanId !== null && r.vlanId > 0 && !known(r.vlanId) ? { ...r, vlanId: 0 } : r;
      }
      if (r.action === 'speed_rebind' || r.action === 'wan_mode') {
        return r.vlanId !== null && !known(r.vlanId) ? { ...r, vlanId: null } : r;
      }
      return 'vlanId' in r && r.vlanId > 0 && !known(r.vlanId) ? { ...r, vlanId: 0 } : r;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.oltId, filter.vlan, vlanQuery.isPending, vlanOptions.length]);

  /** Every VLAN this action needs is chosen — the Build button's precondition. */
  const vlanChosen =
    request.action === 'vlan_change'
      ? request.fromVlanId > 0 && request.toVlanId > 0 && request.fromVlanId !== request.toVlanId
      : request.action === 'attach_vlan' ||
          request.action === 'vlan_mode' ||
          request.action === 'hotspot'
        ? request.vlanId > 0
        : true;

  /**
   * What this chassis refuses, before a plan is built.
   *
   * The server refuses on its own — twice, and on the rows it froze itself —
   * so this is only about not sending somebody to build a plan whose every row
   * comes back with the same sentence on it.
   */
  const chosenOlt = (olts.data?.items ?? []).find((o) => o.id === filter.oltId) ?? null;
  const gateReason = chosenOlt
    ? (catalogue.data?.vendorGate?.[request.action]?.[
        oltFamily(chosenOlt.vendor as OltVendor, chosenOlt.model)
      ] ?? null)
    : null;

  /**
   * Everything an SSID action needs before a plan is worth building.
   *
   * The server refuses each of these on its own — an empty name and a short
   * passphrase are both parse errors at the boundary — so this only exists so
   * the operator is not sent to build a plan that comes back a 400. The open
   * acknowledgement is here for a different reason: it is the one field whose
   * absence is a decision rather than an omission, and the Build button staying
   * off until it is ticked is what makes ticking it an act.
   */
  const ssidReady =
    request.action === 'ssid_set'
      ? request.ssid.trim().length > 0 &&
        request.bands.length > 0 &&
        // Access VLAN chosen but no VLAN picked yet.
        request.vlanId !== 0 &&
        (request.security.kind === 'open'
          ? request.security.acknowledgeOpen
          : request.security.passphrase.length >= 8)
      : request.action === 'ssid_remove'
        ? /*
            Every addressed band needs its interface named. Nothing defaults
            here — an empty box is not "the primary", it is an unanswered
            question, and the primary is the subscriber's own network. The
            same rule is the schema's (`createBulkPlanSchema`), so the button
            stays off rather than sending the operator to collect a 400.
          */
          request.bands.length > 0 &&
          request.bands.every((b) => (b === '2.4G' ? request.port24 : request.port5g))
        : request.action === 'hotspot'
          ? request.ssid.trim().length > 0 &&
            // A disable carries no key; an enable is refused at the boundary without one.
            (!request.enabled ||
              (request.security?.kind === 'wpa' && request.security.passphrase.length >= 8))
          : true;

  /**
   * A rebind that names neither direction is refused at the boundary — see
   * `createBulkPlanSchema` — so the Build button stays off rather than sending
   * the operator to collect a 400.
   */
  const speedReady =
    request.action === 'speed_rebind'
      ? request.upProfile !== null || request.downProfile !== null
      : true;

  /**
   * The WAN-mode form's two boundary rules — dual stack with both modes None,
   * a DNS that is not an address — held here so the operator is not sent to
   * collect the schema's 400 (`lib/bulk-wan-mode.ts`).
   */
  const wanReady =
    request.action === 'wan_mode'
      ? wanModeReady(request)
      : request.action === 'wan_ipv6' &&
          request.ipv6.protocol === 'dual_stack' &&
          request.ipv6.addressMode === 'none' &&
          request.ipv6.prefixMode === 'none'
        ? {
            ready: false,
            reason:
              'Dual stack with neither an IPv6 address mode nor a prefix mode is IPv4 only — choose one, or choose IPv4 only.',
          }
        : { ready: true, reason: null };

  const readyToPlan =
    editable &&
    Boolean(filter.oltId) &&
    (preview.data?.count ?? 0) > 0 &&
    vlanChosen &&
    ssidReady &&
    speedReady &&
    wanReady.ready;

  return (
    <>
      <PageHeader
        title="Bulk actions"
        description="Filter the fleet, review exactly what every matching ONU would receive, then confirm the count. The plan is what runs — not the filter."
      />

      {limits && <CapBanner limits={limits} canEdit={can('infra.manage')} />}

      {/*
        A carried-in selection has to be VISIBLE, because it is a constraint the
        form cannot otherwise show: every filter below can be widened and the
        count will still not exceed the ticked set, which reads as a bug if
        nothing says why. It is also the reason the plan is safe — the ONUs that
        run are the ones that were on screen — so it belongs at the top, with
        the way out beside it.
      */}
      {filter.onuIds && filter.onuIds.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary-soft px-4 py-3 text-sm">
          <span className="text-ink">
            <b>
              {filter.onuIds.length} ONU{filter.onuIds.length === 1 ? '' : 's'} carried from the ONU
              list.
            </b>{' '}
            <span className="text-muted">
              The plan is limited to exactly these, whatever the filters below say.
            </span>
          </span>
          <Button variant="ghost" onClick={() => patch({ onuIds: null })}>
            Use the filters instead
          </Button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Matching ONUs"
          value={preview.isLoading ? '…' : (preview.data?.count ?? 0)}
          icon={<ListChecks className="size-4" />}
          hint={preview.data ? `${preview.data.count} ONU(s)` : undefined}
        />
        <StatCard
          label="PON ports touched"
          value={preview.data?.perPonPort.length ?? 0}
          hint={preview.data ? preview.data.olt.name : 'Choose an OLT'}
        />
        <StatCard
          label="Bulk limit"
          value={limits ? (limits.maxTargets ?? 'all') : '…'}
          tone={preview.data && !preview.data.withinCap ? 'danger' : 'default'}
          hint={
            limits
              ? limits.maxTargetsPerOlt === null
                ? 'no per-chassis limit'
                : `${limits.maxTargetsPerOlt} per chassis`
              : undefined
          }
        />
        <StatCard label="Would be written" value={plan ? plan.eligibleCount : '—'} />
      </div>

      {error ? <div className="mt-4"><ErrorNotice error={error} /></div> : null}
      {notice ? (
        <div className="mt-4 rounded-lg border border-success/30 bg-success-soft px-4 py-3 text-sm text-success">
          {notice}
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
      </div>

      {runningId !== null && live.data && (
        /*
          The run in flight, front and centre — whoever started it, whichever
          tab. Number and chassis first (the operator's names for it), the bar,
          the live counts, and the one control a running fleet operation needs:
          Stop, which halts at the next ONU boundary. When the run ends with
          targets never reached, the same card turns into Continue.
        */
        <Card className="mt-5 border-primary/40">
          <div className="grid gap-3 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-sm font-semibold">
                {live.data.status === 'running' ? (
                  <Loader2 className="size-4 animate-spin text-primary" />
                ) : (
                  <Check className="size-4 text-success" />
                )}
                Run #{live.data.number ?? '—'} · {live.data.action.replace(/_/g, ' ')} on{' '}
                {live.data.oltName}
              </span>
              {live.data.status === 'running' ? (
                <Button
                  size="sm"
                  variant="danger"
                  loading={stopRun.isPending}
                  onClick={() => stopRun.mutate(runningId)}
                >
                  Stop after current ONU
                </Button>
              ) : live.data.abandoned > 0 ? (
                <Button
                  size="sm"
                  variant="primary"
                  loading={resumeMutation.isPending}
                  onClick={() => resumeMutation.mutate(runningId)}
                >
                  Continue — {live.data.abandoned} left
                </Button>
              ) : null}
            </div>
            <RunProgress progress={progress} eligible={live.data.eligibleCount} />
            {live.data.status !== 'running' && live.data.note && (
              <p className="text-xs text-muted">{live.data.note}</p>
            )}
          </div>
        </Card>
      )}

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <Card title="1 · Which ONUs" bodyClassName="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field
              label="OLT — required"
              hint="A bulk run targets one chassis. Everything below is read from it."
            >
              <select
                className="input"
                value={filter.oltId}
                onChange={(e) => chooseOlt(e.target.value)}
              >
                <option value="">Choose a chassis</option>
                {/*
                  In service only. The API refuses a disabled chassis BY NAME at
                  the plan, so offering one here is offering a refusal — and the
                  exception the ONU list makes (a saved link naming one) has no
                  counterpart on a form that always starts empty.
                */}
                {(olts.data?.items ?? [])
                  .filter((o) => !o.disabledAt)
                  .map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} ({o.vendor.toUpperCase()})
                    </option>
                  ))}
              </select>
            </Field>
          </div>
          <Field
            label="PON port"
            hint={
              ponPortQuery.isPending && filter.oltId
                ? 'Reading the chassis…'
                : ponPortOptions.length === 0
                  ? 'No ports recorded yet — sync the OLT.'
                  : `${ponPortOptions.length} on this chassis.`
            }
          >
            <select
              className="input"
              value={filter.ponPort ?? ''}
              onChange={(e) => patch({ ponPort: e.target.value || null })}
              disabled={ponPortOptions.length === 0}
            >
              <option value="">Every port</option>
              {ponPortOptions.map((p) => (
                <option key={p.id} value={p.port}>
                  {p.port}
                  {p.description ? ` · ${p.description}` : ''}
                  {` · ${p.onuCount} ONU${p.onuCount === 1 ? '' : 's'}`}
                  {p.operStatus !== 'up' ? ` · ${p.operStatus}` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="State">
            <select
              className="input"
              value={filter.state ?? ''}
              onChange={(e) => patch({ state: (e.target.value || null) as BulkOnuFilter['state'] })}
            >
              <option value="">Any state</option>
              {/*
                From the schema, not typed out again. This list was a hand-copy
                and it drifted: it went on offering `unconfigured` after that
                state turned out to be produced by no parser on either vendor,
                so choosing it planned a run against zero ONUs — a filter that
                could never be right and never looked wrong. ONUs the chassis
                has seen but not registered are a different thing with their
                own page; they have no row here to carry a state at all.
              */}
              {onuStateSchema.options.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Managed as"
            hint="What the chassis reports, not what was last asked for."
          >
            <select
              className="input"
              value={filter.managementState ?? ''}
              onChange={(e) =>
                patch({
                  managementState: (e.target.value || null) as BulkOnuFilter['managementState'],
                  /*
                    The intent filter is cleared whenever the observed one is
                    set. Both applied at once is an AND of a fact and a stale
                    guess about the same ONU, which on this fleet excludes 159
                    rows for no reason an operator could see.
                  */
                  managementMode: null,
                })
              }
            >
              <option value="">Any</option>
              <option value="tr069">TR-069 — our ACS</option>
              <option value="tr069_other">TR-069 (other) — someone else&rsquo;s ACS</option>
              <option value="omci">OMCI</option>
            </select>
          </Field>
          {/*
            Said here rather than left to be discovered on the plan. A TR-069
            action against these ONUs is not refused — it is composed, sent, and
            has no effect, because the ACS it would run through is not ours.
          */}
          <label className="col-span-full flex cursor-pointer items-start gap-2 text-xs text-muted">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={force}
              onChange={(e) => {
                setForce(e.target.checked);
                // The plan describes the old answer the moment this changes.
                setPlan(null);
                resetConfirm();
              }}
            />
            <span>
              <span className="font-medium text-ink">Re-send even if already set</span> — by default
              an ONU is skipped when SimpleACS&rsquo;s record says it already has the profile, the
              VLAN or the mode. That record can be out of step with the chassis. Tick this to write
              anyway; the row still says what was noticed. Anything the chassis or the hardware
              refuses is unaffected.
            </span>
          </label>
          {filter.managementState === 'tr069_other' && (
            <p className="col-span-full -mt-1 text-xs text-warn">
              These ONUs answer to an ACS this organization does not run. Actions that go through
              the ACS cannot reach them; actions written to the chassis over OMCI can.
            </p>
          )}
          <Field
            label="Attached VLAN"
            hint={
              vlanOptions.length === 0 && filter.oltId
                ? 'No VLANs recorded yet — sync the OLT.'
                : 'Every VLAN this chassis carries. Filtering is a read, so the management one is offered here.'
            }
          >
            <select
              className="input"
              value={filter.vlan ?? ''}
              onChange={(e) => patch({ vlan: e.target.value ? Number(e.target.value) : null })}
              disabled={vlanOptions.length === 0}
            >
              <option value="">Any VLAN</option>
              {vlanOptions.map((v) => (
                <option key={v.id} value={v.vlanId}>
                  {vlanLabel(v)}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Model"
            hint={
              onuTypeOptions.length === 0 && filter.oltId
                ? 'No models recorded yet — sync the OLT.'
                : 'As the chassis reports it.'
            }
          >
            <select
              className="input"
              value={filter.onuType ?? ''}
              onChange={(e) => patch({ onuType: e.target.value || null })}
              disabled={onuTypeOptions.length === 0}
            >
              <option value="">Any model</option>
              {onuTypeOptions.map((t) => (
                <option key={t.onuType} value={t.onuType}>
                  {t.onuType} · {t.count}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="WAN mode"
            hint="From recorded VLAN intent. An ONU whose config was never adopted here records neither — open its page once, or authorise it here, and it counts."
          >
            <select
              className="input"
              value={filter.wanMode ?? ''}
              onChange={(e) =>
                patch({ wanMode: (e.target.value || null) as BulkOnuFilter['wanMode'] })
              }
            >
              <option value="">Either</option>
              <option value="bridge">Bridged — the router behind the ONU does PPPoE/DHCP</option>
              <option value="route">Routed — the ONU terminates the WAN itself</option>
            </select>
          </Field>
          <Field label="On an ACS">
            <select
              className="input"
              value={filter.hasDevice === null ? '' : filter.hasDevice ? 'yes' : 'no'}
              onChange={(e) =>
                patch({ hasDevice: e.target.value === '' ? null : e.target.value === 'yes' })
              }
            >
              <option value="">Either</option>
              <option value="yes">Has informed</option>
              <option value="no">Never informed</option>
            </select>
          </Field>
          <Field
            label="Name starts with"
            hint="Your own naming convention as a selector — e.g. HS selects the hotspot units. ONUs with no name never match."
          >
            <Input
              value={filter.nameStartsWith ?? ''}
              onChange={(e) => patch({ nameStartsWith: e.target.value || null })}
              placeholder="HS"
            />
          </Field>
          <Field
            label="Name does not start with"
            hint="The rest of the fleet — e.g. HS here selects everything except the hotspot units, unnamed ONUs included."
          >
            <Input
              value={filter.nameNotStartsWith ?? ''}
              onChange={(e) => patch({ nameNotStartsWith: e.target.value || null })}
              placeholder="HS"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Search" hint="Serial, name or interface.">
              <Input
                value={filter.search ?? ''}
                onChange={(e) => patch({ search: e.target.value || null })}
                placeholder="RTEGC6A2955E"
              />
            </Field>
          </div>
          {preview.data && preview.data.perPonPort.length > 0 && (
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              {preview.data.perPonPort.map((p) => (
                <Pill key={p.ponPort} tone="neutral">
                  {p.ponPort}: {p.count}
                </Pill>
              ))}
            </div>
          )}
          {!filter.oltId && (
            <p className="sm:col-span-2 text-xs text-faint">
              Choose a chassis to see how many ONUs match.
            </p>
          )}
          {preview.data && !preview.data.withinCap && (
            <p className="sm:col-span-2 text-xs text-warn">{preview.data.capReason}</p>
          )}
        </Card>

        <Card title="2 · What to do" bodyClassName="grid gap-3 p-5">
          <Field label="Action" hint={ACTION_HELP[request.action]}>
            <select
              className="input"
              /*
                `ssid_remove` has no option of its own any more — it is the
                second mode of "Add/Remove SSID" — so it selects that entry
                here. Without this a deep link (`?action=ssid_remove`, which
                is how another page hands an action over) would render a blank
                select.
              */
              value={request.action === 'ssid_remove' ? 'ssid_set' : request.action}
              onChange={(e) => {
                setPlan(null);
                resetConfirm();
                setRequest(defaultRequest(e.target.value));
              }}
            >
              <option value="device_preset">Device config preset</option>
              <option value="management_mode">Management mode</option>
              <option value="tr069_profile">TR-069 profile</option>
              <option value="attach_vlan">Attach a VLAN</option>
              {/*
                "Routed / bridged" (`vlan_mode`) was here until 2026-09-14. The
                operator asked for every option of the ONU page's WAN dialog in
                bulk, and `wan_mode` is that dialog's Apply per ONU — the OLT
                half `vlan_mode` wrote, plus the WAN object, the addressing, the
                IP protocol and the firewall. The old id stays parseable for
                the runs in the ledger; it is just no longer offered.
              */}
              <option value="wan_mode">WAN mode — routed or bridged, addressing, IP protocol, firewall</option>
              <option value="vlan_change">Move to another VLAN</option>
              {/*
                ONE entry for the pair (operator, 2026-09-24: "change add or set
                an ssid on band to Add/Remove SSID"). Adding a network and taking
                one away are the same errand to an operator, and they were two
                lines apart in a list of eighteen. The Mode field inside swaps
                which action is sent; both ids stay, because the ledger holds
                runs under each.
              */}
              <option value="ssid_set">Add/Remove SSID</option>
              <option value="zone">Zone</option>
              <option value="firmware_campaign">Firmware upgrade campaign</option>
              <option value="onu_resync">Resync — re-state the stored config</option>
              <option value="onu_adopt">Import — read and record each ONU’s config</option>
              <option value="onu_summon">Summon — wake each CPE and refresh its details</option>
              {/*
                The firewall shipped with a form, a default and a server-side
                write, and never got a line here — so the only way to reach it
                was the `?action=` deep link. A control an operator cannot see
                is not a control; this is the one line that was missing.
              */}
              <option value="onu_firewall">Firewall — answer or ignore the WAN side</option>
              <option value="wan_ipv6">IP protocol — IPv6 on the subscriber WAN, on its own</option>
              <option value="router_clock">Router clock — East Africa Time, kept by NTP</option>
              <option value="speed_rebind">Speed — bind to another profile the chassis has</option>
              <option value="onu_reboot">Reboot through the chassis</option>
              <option value="onu_recreate">Recreate — deauthorise and rebuild</option>
            </select>
          </Field>

          {/*
            What kind of thing this is, said before the plan is built rather
            than after. An operator who reaches the confirmation box and only
            then learns it is a reboot has already made the decision.
          */}
          <p className={`-mt-1 text-xs ${RISK_NOTE[BULK_ACTION_RISK[request.action]].tone}`}>
            {RISK_NOTE[BULK_ACTION_RISK[request.action]].what}
          </p>
          {gateReason && (
            <p className="text-xs text-warn">
              <strong>Not available on this chassis.</strong> {gateReason}
            </p>
          )}

          <ActionFields
            request={request}
            setRequest={(r) => {
              setPlan(null);
              resetConfirm();
              setRequest(r);
            }}
            presets={presets.data?.items ?? []}
            zones={topology.data?.zones ?? []}
            profiles={oltProfiles.data?.items ?? []}
            speedProfiles={speedProfileItems}
            speedProfilesLoading={speedProfiles.isPending && speedProfiles.fetchStatus !== 'idle'}
            subscriberVlans={subscriberVlans}
            moveFromVlans={moveFromVlans}
            managementVlans={managementVlans}
            vlan1Withheld={vlanOffer.vlan1Withheld}
            oltChosen={Boolean(filter.oltId)}
            oltVendor={chosenOlt?.vendor ?? null}
          />

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant="primary"
              loading={planMutation.isPending}
              disabled={!readyToPlan}
              onClick={() => planMutation.mutate()}
            >
              Build the plan
            </Button>
            <span className="text-xs text-faint">
              {!filter.oltId
                ? 'Choose a chassis first — a bulk run targets one OLT.'
                : !vlanChosen
                  ? 'Choose the VLAN this acts on.'
                  : !speedReady
                    ? 'Name the upstream profile, the downstream profile, or both.'
                    : !wanReady.ready
                      ? wanReady.reason
                      : 'A plan writes nothing. It shows exactly what each ONU would receive.'}
            </span>
          </div>
        </Card>
      </div>

      {plan && (
        <div className="mt-4">
          <Card
            title={`3 · Review — ${plan.eligibleCount} of ${plan.targetCount} on ${plan.oltName ?? plan.oltId} would be written`}
            action={
              !ran && editable ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => discardMutation.mutate(plan.id)}
                >
                  Discard
                </Button>
              ) : null
            }
          >
            <div className="border-b border-line px-5 py-4">
              {ran ? (
                <div className="grid gap-2">
                  <p className="text-sm text-muted">
                    Run by {plan.executedBy} at {formatTimestamp(plan.executedAt)} —{' '}
                    {plan.succeeded} applied
                    {plan.failed > 0 ? `, ${plan.failed} failed` : ''}
                    {plan.skipped > 0 ? `, ${plan.skipped} skipped` : ''}
                    {plan.abandoned > 0 ? `, ${plan.abandoned} never reached` : ''}.
                    {plan.note ? ` (${plan.note})` : ''}
                  </p>
                  {/*
                    The half-done run, and the way out of it.

                    A plan still runs exactly once — this does not re-run it.
                    It plans the targets the run never reached as a NEW draft,
                    resolved against the fleet as it is now and confirmed
                    again, which is the only honest way to finish work that was
                    authorised against a fleet this old.
                  */}
                  {plan.abandoned > 0 && editable ? (
                    <div className="flex items-start gap-2 text-sm text-warn">
                      <ShieldAlert className="mt-0.5 size-4 shrink-0" />
                      <span>
                        This run stopped before reaching {plan.abandoned} ONU
                        {plan.abandoned === 1 ? '' : 's'}. Nothing was written to them and nothing
                        was decided about them.{' '}
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={resumeMutation.isPending}
                          onClick={() => resumeMutation.mutate(plan.id)}
                        >
                          Plan the remaining {plan.abandoned}
                        </Button>
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : plan.overCap ? (
                <div className="flex items-start gap-2 text-sm text-danger">
                  <ShieldAlert className="mt-0.5 size-4 shrink-0" />
                  <span>
                    This plan would write to {plan.eligibleCount} ONUs and the limit is {plan.cap}{' '}
                    ({plan.capPerOlt === null ? 'no per-chassis limit' : `${plan.capPerOlt} per chassis`}).
                    Narrow the filter, or change the limit deliberately — it is a superadmin change
                    and it is recorded.
                  </span>
                </div>
              ) : (
                <div className="grid gap-3">
                  {/*
                    The rungs above the count, and the reason they are here at
                    all: a full-fleet reboot has to be harder to press than a
                    resync, or the numeric cap was the only thing making it
                    hard and the cap now defaults to "all". Which fields appear
                    comes from `bulkConfirmation`, computed from the plan's own
                    action and filter — the same call the server makes before it
                    writes anything, so this can never ask for less than the
                    server insists on.
                  */}
                  {(needed.oltName || needed.phrase) && (
                    <div
                      className={`rounded-lg border px-4 py-3 text-sm ${
                        needed.risk === 'rebuild'
                          ? 'border-danger/30 bg-danger-soft text-danger'
                          : 'border-warn/30 bg-warn-soft text-warn'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <ShieldAlert className="mt-0.5 size-4 shrink-0" />
                        <span>
                          {needed.wholeChassis ? (
                            <>
                              This covers <strong>every ONU on {planOltName}</strong> —{' '}
                              {plan.eligibleCount} subscriber
                              {plan.eligibleCount === 1 ? '' : 's'}, nothing filtered out.{' '}
                            </>
                          ) : (
                            <>
                              This affects {plan.eligibleCount} subscriber
                              {plan.eligibleCount === 1 ? '' : 's'} on {planOltName}.{' '}
                            </>
                          )}
                          {RISK_NOTE[needed.risk].what}
                        </span>
                      </div>
                    </div>
                  )}

                  {runningId !== null ? (
                    <RunProgress progress={progress} eligible={plan.eligibleCount} />
                  ) : (
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        variant="danger"
                        icon={<Play className="size-4" />}
                        disabled={!editable}
                        onClick={() => setConfirmOpen(true)}
                      >
                        Apply to {plan.eligibleCount} ONU{plan.eligibleCount === 1 ? '' : 's'} on{' '}
                        {plan.oltName ?? 'this OLT'}
                      </Button>
                      <span className="text-xs text-faint">
                        Expires {formatRelative(plan.expiresAt)} — the fleet moves, so a stale plan
                        is refused rather than run.
                      </span>
                    </div>
                  )}

                  {confirmOpen && (
                    <ConfirmRunDialog
                      plan={plan}
                      needed={needed}
                      oltName={planOltName}
                      pending={runMutation.isPending}
                      onCancel={() => setConfirmOpen(false)}
                      onConfirm={() => runMutation.mutate(plan.id)}
                    />
                  )}
                </div>
              )}
            </div>

            {/*
              Every refusal on the plan, gathered by reason, above the rows
              rather than inside them.

              This is what stops a correct run from reading as a broken one. A
              whole-chassis "5 GHz SSID" plan is 287 ONUs that will be written
              and 2,165 Huawei HG8546Ms that have no 5 GHz radio, and scrolling
              two thousand identical sentences is indistinguishable from
              scrolling two thousand errors. They are not errors: a refused
              target is counted out of the number typed back, is never
              executed, and never gets an outcome. Said once with its own
              count, that is legible — and the alternative, leaving them out,
              would let a run that writes nothing look like a run that worked.

              The server derives this from the frozen rows, so it cannot
              disagree with the table underneath it.
            */}
            {plan.refusals.length > 0 && (
              <div className="border-b border-line px-5 py-4">
                <div className="text-sm font-medium text-ink">
                  {plan.targetCount - plan.eligibleCount} of {plan.targetCount} would not be
                  written
                </div>
                <ul className="mt-2 grid gap-2">
                  {plan.refusals.map((group) => (
                    <li key={group.reason} className="flex items-start gap-2 text-sm text-muted">
                      <span className="mt-0.5 shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-xs font-medium text-ink">
                        {group.count}
                      </span>
                      <span>
                        {group.reason}
                        {group.count > group.serials.length ? (
                          <span className="text-xs text-faint"> — e.g. {group.serials.join(', ')}</span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Table>
              <THead>
                <th className="th">ONU</th>
                <th className="th">PON port</th>
                <th className="th">What it gets</th>
                <th className="th">Outcome</th>
              </THead>
              <TBody>
                {plan.targets.map((t) => (
                  <TRow key={t.onuId}>
                    <td className="td">
                      <div className="font-medium text-ink">{t.name ?? t.serial}</div>
                      <div className="text-xs text-faint">
                        {t.serial}
                        {t.interfaceName ? ` · ${t.interfaceName}` : ''}
                      </div>
                    </td>
                    <td className="td text-sm">
                      {/* Every row is on the same chassis now, so the column
                          that earns its place is the port. */}
                      {t.interfaceName?.replace(/^gpon-onu_/, '').split(':')[0] ?? '—'}
                      <div className="text-xs text-faint">{t.vendor?.toUpperCase()}</div>
                    </td>
                    <td className="td">
                      {t.applicable ? (
                        <>
                          {t.summary.map((s, i) => (
                            <div key={i} className="text-sm text-ink">
                              {s}
                            </div>
                          ))}
                          {t.warnings.map((w, i) => (
                            <div key={`w${i}`} className="text-xs text-warn">
                              {w}
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          {t.blockers.map((b, i) => (
                            <div key={i} className="text-sm text-muted">
                              {b}
                            </div>
                          ))}
                          {/*
                            What it WOULD have been, when the resolution got far
                            enough to work it out.
                            Most refusals stop before there is anything to show
                            and this renders nothing. The ones that do not are
                            the ones worth reading: a VLAN move refused because
                            no chassis here has a verified removal command has
                            still worked out which subscribers would move and
                            exactly what would be sent, and that is the thing
                            somebody takes to a supervised test.
                          */}
                          {t.summary.map((s, i) => (
                            <div key={`s${i}`} className="mt-1 text-xs text-faint">
                              Would be: {s}
                            </div>
                          ))}
                        </>
                      )}
                    </td>
                    <td className="td">
                      <OutcomePill target={t} />
                      {t.message && <div className="mt-1 text-xs text-faint">{t.message}</div>}
                    </td>
                  </TRow>
                ))}
              </TBody>
            </Table>
          </Card>
        </div>
      )}

      <div className="mt-4">
        <Card title="Task history">
          {history.isLoading ? (
            <div className="p-6">
              <Spinner />
            </div>
          ) : (history.data?.items ?? []).length === 0 ? (
            <EmptyState
              title="No bulk runs yet"
              description="Every plan, executed or not, is kept here with its per-ONU outcome."
            />
          ) : (
            <Table>
              <THead>
                <th className="th">Run</th>
                <th className="th">When</th>
                <th className="th">OLT</th>
                <th className="th">Action</th>
                <th className="th">Who</th>
                <th className="th">Result</th>
              </THead>
              <TBody>
                {(history.data?.items ?? []).map((p) => (
                  <TRow
                    key={p.id}
                    onClick={async () => {
                      const full = await api.get<BulkPlan>(`/bulk-actions/plans/${p.id}`);
                      setPlan(full);
                      resetConfirm();
                      setNotice(null);
                    }}
                  >
                    <td className="td text-sm font-medium tabular-nums">#{p.number ?? '—'}</td>
                    <td className="td text-sm">
                      {formatTimestamp(p.executedAt ?? p.createdAt)}
                      <div className="text-xs text-faint">
                        {formatRelative(p.executedAt ?? p.createdAt)}
                      </div>
                    </td>
                    {/* Permanent, from the row's own column: "which OLT did
                        that run on" survives the chassis being renamed. */}
                    <td className="td text-sm">{p.oltName ?? p.oltId}</td>
                    <td className="td text-sm">{p.action.replace(/_/g, ' ')}</td>
                    <td className="td text-sm">{p.executedBy ?? p.createdBy}</td>
                    <td className="td">
                      {p.status === 'draft' ? (
                        <Pill tone="neutral">
                          planned · {p.eligibleCount} of {p.targetCount}
                        </Pill>
                      ) : p.status === 'running' ? (
                        <Pill tone="info" dot>
                          {(p as { stopRequested?: number | boolean }).stopRequested ? 'stopping' : 'running'} · {p.succeeded + p.failed + p.skipped} of {p.targetCount}
                          {p.failed > 0 ? ` · ${p.failed} failed` : ''}
                        </Pill>
                      ) : p.failed > 0 ? (
                        <Pill tone="danger" dot>
                          completed · {p.succeeded} applied · {p.failed} failed
                          {p.skipped > 0 ? ` · ${p.skipped} skipped` : ''}
                          {p.abandoned > 0 ? ` · ${p.abandoned} never reached` : ''}
                        </Pill>
                      ) : p.abandoned > 0 ? (
                        /**
                         * A run that stopped is not a success with a footnote.
                         *
                         * It used to render as one — the counts came out of
                         * columns the abandoned run never wrote, so half a
                         * chassis showed as "0 applied" in a green pill. The
                         * counts are derived from the target rows now, and a
                         * run with work left on the floor gets a pill that
                         * says so.
                         */
                        <Pill tone="warn" dot>
                          stopped · {p.succeeded} applied · {p.abandoned} never reached
                          {p.skipped > 0 ? ` · ${p.skipped} skipped` : ''}
                        </Pill>
                      ) : (
                        <Pill tone="success" dot>
                          completed · {p.succeeded} applied
                          {p.skipped > 0 ? ` · ${p.skipped} skipped` : ''}
                        </Pill>
                      )}
                    </td>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}

function OutcomePill({ target }: { target: BulkPlan['targets'][number] }) {
  if (target.outcome === 'ok') {
    return (
      <Pill tone="success" dot>
        <Check className="size-3" /> applied
      </Pill>
    );
  }
  if (target.outcome === 'failed') {
    return (
      <Pill tone="danger" dot>
        <X className="size-3" /> failed
      </Pill>
    );
  }
  if (target.outcome === 'skipped') {
    return (
      <Pill tone="warn" dot>
        skipped
      </Pill>
    );
  }
  /**
   * The run stopped before this ONU. It is NOT "ready", which is what it used
   * to render as — a null outcome on an executed plan looked exactly like a
   * plan nobody had run, so a half-finished chassis read as an untouched one
   * and nothing on the screen said which subscribers still needed the change.
   */
  if (target.outcome === 'abandoned') {
    return (
      <Pill tone="warn" dot>
        never reached
      </Pill>
    );
  }
  return target.applicable ? <Pill tone="info">ready</Pill> : <Pill tone="neutral">refused</Pill>;
}

/**
 * The cap, stated where it is felt, and raised in one place.
 *
 * Deliberately loud. An operator who does not know a limit exists reads a
 * refusal as a bug; one who does knows the fleet is protected and where the
 * lever is.
 */
/**
 * The cap, stated where it is felt, and raised in one place.
 *
 * Deliberately loud. An operator who does not know a limit exists reads a
 * refusal as a bug; one who does knows the fleet is protected and where the
 * lever is.
 *
 * Both fields are dropdowns rather than free entry, over the same lists the
 * server validates against — a number typed here that the server would refuse
 * is a round trip spent learning nothing. The lists are asymmetric on purpose:
 * per-run is 1–10 and has no "none", per-chassis is 50–500 and does.
 */
function CapBanner({ limits, canEdit }: { limits: BulkLimits; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [max, setMax] = useState(
    limits.maxTargets === null ? BULK_LIMIT_ALL : String(limits.maxTargets),
  );
  const [perOlt, setPerOlt] = useState(
    limits.maxTargetsPerOlt === null ? BULK_LIMIT_NONE : String(limits.maxTargetsPerOlt),
  );
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch<BulkLimits>('/bulk-actions/limits', {
        // The literal word, not a number and not a null - the same distinction
        // the per-chassis field makes below, for the same reason.
        maxTargets: max === BULK_LIMIT_ALL ? BULK_LIMIT_ALL : Number(max),
        // The literal word, not a number and not a null: null would mean
        // "restore the shipped default", which is the opposite intention.
        maxTargetsPerOlt: perOlt === BULK_LIMIT_NONE ? BULK_LIMIT_NONE : Number(perOlt),
        reason,
      }),
    onSuccess: async () => {
      setOpen(false);
      setReason('');
      setErr(null);
      await queryClient.invalidateQueries({ queryKey: ['bulk-preview'] });
      await queryClient.invalidateQueries({ queryKey: ['bulk-limits'] });
    },
    onError: setErr,
  });

  const atDefault =
    limits.maxTargetsSource === 'default' && limits.maxTargetsPerOltSource === 'default';
  const perChassisRemoved = limits.maxTargetsPerOlt === null;
  const unlimited = limits.maxTargets === null;
  /**
   * A stored value nobody can read.
   *
   * Worth its own sentence because the number on screen is then neither what
   * the operator set nor what ships - it is the safe fallback, and without
   * being told, somebody would reasonably read a sudden 2 as this feature
   * having been quietly locked down.
   */
  const unreadable =
    limits.maxTargetsSource === 'unreadable' || limits.maxTargetsPerOltSource === 'unreadable';

  const maxChoices: number[] = [...BULK_MAX_TARGETS_OPTIONS];
  if (limits.maxTargets !== null && !maxChoices.includes(limits.maxTargets)) {
    maxChoices.unshift(limits.maxTargets);
  }

  /**
   * A value already stored that is not on the list any more.
   *
   * Every install so far stores 2 for the per-chassis limit, which is below
   * the new minimum of 50. The select has to show what is actually in force
   * rather than silently rendering blank or, worse, the first option — an
   * operator opening this panel must see their real setting.
   */
  const perOltChoices: number[] = [...BULK_MAX_TARGETS_PER_OLT_OPTIONS];
  if (
    limits.maxTargetsPerOlt !== null &&
    !perOltChoices.includes(limits.maxTargetsPerOlt)
  ) {
    perOltChoices.unshift(limits.maxTargetsPerOlt);
  }

  return (
    <div className="mb-4 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2 text-sm text-warn">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            {unlimited ? (
              <>
                A bulk run may write to <strong>every ONU it matched</strong> on the chosen chassis
                {perChassisRemoved ? '' : `, up to ${limits.maxTargetsPerOlt} of them`}.
              </>
            ) : (
              <>
                A bulk run may write to at most <strong>{limits.maxTargets}</strong> ONU
                {limits.maxTargets === 1 ? '' : 's'}
                {perChassisRemoved ? (
                  <>
                    {' '}
                    — there is <strong>no separate per-chassis limit</strong>, but a run targets
                    one chassis anyway, so {limits.maxTargets} is still the most it can touch.
                  </>
                ) : (
                  <>
                    , and <strong>{limits.maxTargetsPerOlt}</strong> on any one chassis.
                  </>
                )}
              </>
            )}{' '}
            {unreadable
              ? 'One of these settings could not be read, so the safe value is in force until it is set again. That is deliberate: a value nobody can read is never taken to mean no limit.'
              : atDefault
                ? 'That is the shipped default. What stands between a slip and the fleet is not this number any more — it is the confirmation: the exact count on every run, the chassis\u2019s own name for anything service-affecting, and a word typed out in full for a recreate or a whole-chassis reboot.'
                : `Set by ${limits.updatedBy ?? 'an administrator'} ${formatRelative(limits.updatedAt)}.`}
          </span>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : 'Change the limit'}
          </Button>
        )}
      </div>
      {open && (
        <div className="mt-3 grid gap-3 sm:grid-cols-4">
          <Field
            label="ONUs per run"
            hint="Ships as All. Set a number to put a hard ceiling back"
          >
            <select className="input" value={max} onChange={(e) => setMax(e.target.value)}>
              <option value={BULK_LIMIT_ALL}>All — every ONU the plan matched</option>
              {maxChoices.map((n) => (
                <option key={n} value={n}>
                  {n} ONU{n === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Per chassis" hint="A secondary ceiling, rarely reached.">
            <select className="input" value={perOlt} onChange={(e) => setPerOlt(e.target.value)}>
              {perOltChoices.map((n) => (
                <option key={n} value={n}>
                  {n} ONUs
                </option>
              ))}
              <option value={BULK_LIMIT_NONE}>None (no limit)</option>
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Why" hint="Recorded in the action log with both values.">
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Firmware campaign for the Ruaka rollout, agreed with ops"
              />
            </Field>
          </div>
          {/*
            The interaction, said out loud where the choice is made.
            "No limit" beside a live fleet of 4,635 subscribers reads as "this
            can now touch everything" unless the page says otherwise — and it
            cannot, because a run is one chassis and the per-run cap is
            untouched by this field.
          */}
          <p className="sm:col-span-4 text-xs text-warn">
            {max === BULK_LIMIT_ALL && perOlt === BULK_LIMIT_NONE ? (
              <>
                <strong>No numeric ceiling at all.</strong> A run still targets exactly one chassis,
                still writes only to the ONUs the plan froze, and still cannot start without the
                exact count typed back — plus the chassis name for anything service-affecting and
                a word in full for a recreate or a whole-chassis reboot. Set a number here if you
                want a hard stop as well.
              </>
            ) : max === BULK_LIMIT_ALL ? (
              <>
                <strong>All</strong> removes the per-run ceiling; <strong>{perOlt}</strong> per
                chassis still applies, and a run targets one chassis, so that is what binds.
              </>
            ) : perOlt === BULK_LIMIT_NONE ? (
              <>
                <strong>None removes the per-chassis ceiling only.</strong> A bulk run still targets
                exactly one chassis and still cannot exceed{' '}
                <strong>
                  {max} ONU{Number(max) === 1 ? '' : 's'}
                </strong>{' '}
                per run — that check is separate and stays.
              </>
            ) : (
              <>
                A run targets one chassis, so the smaller of the two is what actually refuses a
                request. Both are recorded on every plan, so a refusal can explain itself months
                later.
              </>
            )}
          </p>
          <div className="sm:col-span-4 flex items-center gap-2">
            <Button
              variant="danger"
              loading={save.isPending}
              disabled={reason.trim().length < 4}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
            {err ? <ErrorNotice error={err} /> : null}
          </div>
        </div>
      )}
    </div>
  );
}

function defaultRequest(action: string): BulkActionRequest {
  switch (action) {
    case 'device_preset':
      return { action: 'device_preset', presetId: '' };
    case 'management_mode':
      return {
        action: 'management_mode',
        mode: 'tr069',
        addressing: 'dhcp',
        mgmtVlan: null,
        tr069ProfileId: null,
      };
    case 'tr069_profile':
      return { action: 'tr069_profile', profileId: null };
    // 0 means "not chosen yet" — the schema's minimum is 1, so an unfilled
    // form cannot be planned. Seeding a plausible number like 100 was how a
    // VLAN nobody picked ended up in a request.
    case 'attach_vlan':
      return { action: 'attach_vlan', vlanId: 0, mode: 'route', name: null };
    // Not offered in the picker since 2026-09-14 (`wan_mode` carries everything
    // it did; the `?action=vlan_mode` link is translated on mount). Still in the
    // request union for the ledger's runs, so the switch stays complete.
    case 'vlan_mode':
      return { action: 'vlan_mode', vlanId: 0, mode: 'bridge' };
    /**
     * Null VLAN = each ONU's own subscriber VLAN — the dialog opens on the
     * ONU's own VLAN, and there is no "unchosen" here because "as recorded" is
     * a real choice, unlike `attach_vlan`'s 0. Routed because the
     * TR-069-default fleet's WANs are routed; every other value is the step's
     * own opening value (`lib/bulk-wan-mode.ts`).
     */
    case 'wan_mode':
      return defaultWanModeRequest();
    // Both ends unchosen, for the same reason: a seeded number is a VLAN
    // nobody picked, and here it would be a VLAN nobody picked to move OFF.
    case 'vlan_change':
      return { action: 'vlan_change', fromVlanId: 0, toVlanId: 0, mode: null };
    /**
     * Both bands and both interfaces unchosen, which is the ordinary request
     * ("the house network on both radios") and also the safest default: null
     * is each band's primary, and the primary is the one interface every model
     * on this fleet has.
     *
     * WPA is the seeded security and there is no seeded key — the schema's
     * minimum is 8, so an unfilled form cannot be planned, and "open" is never
     * where an unfilled form lands.
     */
    case 'ssid_set':
      return {
        action: 'ssid_set',
        ssid: '',
        bands: ['2.4G', '5G'],
        port24: null,
        port5g: null,
        // No VLAN by default: naming a radio and binding it are separate asks.
        vlanId: null,
        security: { kind: 'wpa', mode: 'wpa-wpa2-psk', passphrase: '' },
      };
    /**
      * No interface and no band assumed: an operator says WHICH WiFi to turn
      * off, on which radio. Opening on both bands with the primaries filled in
      * would be a form whose default run takes every subscriber's own network
      * off the air.
      */
    case 'ssid_remove':
      return {
        action: 'ssid_remove',
        ssid: null,
        port24: null,
        port5g: null,
        bands: ['2.4G'],
        delete: false,
      };
    /**
     * On, the operator's default name, the second slot on each radio, and no
     * VLAN chosen (0 — the schema's minimum is 1, so an unfilled form cannot
     * be planned). WPA with an empty key for the same reason `ssid_set` seeds
     * it: the minimum is 8, and "open" is never where an unfilled form lands.
     */
    case 'hotspot':
      return {
        action: 'hotspot',
        enabled: true,
        vlanId: 0,
        // One WORD: the ZTE chassis parser stops at a space (bulkSsidNameSchema).
        ssid: 'MymangaPublicWiFi',
        security: { kind: 'wpa', mode: 'wpa-wpa2-psk', passphrase: '' },
        port24: 'wifi_0/2',
        port5g: 'wifi_0/6',
      };
    case 'onu_resync':
      return { action: 'onu_resync' };
    case 'onu_adopt':
      return { action: 'onu_adopt' };
    case 'onu_summon':
      return { action: 'onu_summon' };
    /**
     * Opens on PD with no IPv6 address — the shape the operator asked for
     * (2026-09-23, "ipv6 pd with ipv6 set on none"): the WAN takes no IPv6
     * address of its own and asks only for a delegated prefix for the LAN
     * behind it.
     */
    case 'wan_ipv6':
      return { action: 'wan_ipv6', ipv6: { protocol: 'dual_stack', addressMode: 'none', prefixMode: 'dhcpv6_pd' } };
    case 'router_clock':
      return { action: 'router_clock' };
    case 'onu_firewall':
      // Defaults to ON, and to the whole ONU. The safe direction is the one
      // that does not put a subscriber's router on the WAN by accepting a
      // default; the scope's default is the write this action has always sent,
      // so the field's arrival changes nothing for anyone already using it.
      return { action: 'onu_firewall', enabled: true, scope: 'all' };
    /**
     * Both directions unchosen, and unchosen means "leave it alone" rather
     * than a seeded profile name.
     *
     * Same reason the VLAN actions seed 0: a plausible default here would be a
     * profile nobody picked, and on this fleet the plausible default is
     * `SMARTOLT-1G-DOWN` — the name 2,120 service-ports are already bound to.
     * The Build button stays off until one of the two is named.
     */
    case 'speed_rebind':
      return { action: 'speed_rebind', upProfile: null, downProfile: null, vlanId: null };
    case 'onu_reboot':
      return { action: 'onu_reboot' };
    case 'onu_recreate':
      return { action: 'onu_recreate' };
    case 'firmware_campaign':
      return {
        action: 'firmware_campaign',
        name: '',
        fileName: '',
        targetVersion: '',
        batchSize: 1,
        pauseOnFailure: true,
      };
    default:
      return { action: 'zone', zoneId: null };
  }
}

/**
 * `VLAN 12 · data · 146 ONUs on the OLT, read 11d ago` — enough to pick the
 * right one without guessing, and enough to see when the number is old.
 *
 * The chassis figure and SimpleACS's own are labelled apart, because they
 * answer different questions and on this fleet the second is zero. And the
 * chassis figure carries its age: nothing keeps it current, it is written only
 * when somebody runs Detect management VLAN on the OLT's VLANs tab, and every
 * gachie1 row still holds one timestamp from a single 2026-08-19 run. A label
 * that shows the count and hides the date invites picking a VLAN on the
 * strength of a number that was true months ago.
 */
function vlanLabel(v: OltVlan): string {
  const name = v.description?.trim() || v.name?.trim() || null;
  const parts = [`VLAN ${v.vlanId}`];
  if (name && name !== `VLAN${String(v.vlanId).padStart(4, '0')}`) parts.push(name);
  if (v.usedForMgmt) parts.push('management');
  if (v.chassisOnuCount !== null && v.chassisOnuCount !== undefined) {
    const n = v.chassisOnuCount;
    parts.push(
      `${n} ONU${n === 1 ? '' : 's'} on the OLT${
        v.chassisOnuCountAt ? `, read ${formatRelative(v.chassisOnuCountAt)}` : ''
      }`,
    );
  } else if (v.onuCount > 0) {
    parts.push(`${v.onuCount} ONU${v.onuCount === 1 ? '' : 's'}`);
  }
  if (v.uplinks.length === 0) parts.push('not on an uplink');
  return parts.join(' · ');
}

function ActionFields({
  request,
  setRequest,
  presets,
  zones,
  profiles,
  speedProfiles,
  speedProfilesLoading,
  subscriberVlans,
  moveFromVlans,
  managementVlans,
  vlan1Withheld,
  oltChosen,
  oltVendor,
}: {
  request: BulkActionRequest;
  setRequest: (r: BulkActionRequest) => void;
  presets: Array<{ id: string; name: string; target: string }>;
  zones: Zone[];
  /** The chosen chassis's associated profiles, its default marked. */
  profiles: Array<{ profileId: string; name: string; isDefault: boolean }>;
  /** The bandwidth profiles the chosen chassis itself holds. */
  speedProfiles: SpeedProfileOption[];
  /** The chassis read is still in flight — an empty list does not mean none. */
  speedProfilesLoading: boolean;
  /** Only what a subscriber may be put on — never the management VLAN. */
  subscriberVlans: OltVlan[];
  /**
   * Where a move may start FROM: every VLAN this chassis carries that it does
   * not manage ONUs on. Wider than `subscriberVlans` on purpose — a source is
   * a statement about where subscribers already are, and moving them OFF a
   * VLAN the tenant has stopped offering is the reason to stop offering it.
   */
  moveFromVlans: OltVlan[];
  /** Only what this chassis manages ONUs on — never a subscriber VLAN. */
  managementVlans: OltVlan[];
  /** The chassis carries VLAN 1 and the org has it switched off. */
  vlan1Withheld: boolean;
  oltChosen: boolean;
  /**
   * The chosen chassis's vendor. The WAN-mode form is per chassis the way the
   * dialog's step list is (`wanWizardSteps({ firewallApplicable: isZte })`):
   * the Firewall step is absent on a Huawei chassis, and PPPoE/static are its
   * CPE's job. One run is one chassis, so one vendor decides the form.
   */
  oltVendor: string | null;
}) {
  const defaultProfile = profiles.find((p) => p.isDefault) ?? profiles[0] ?? null;
  /*
    The management run's profile fills itself in with the chassis's default —
    and follows a chassis change, because a profile id from the previous OLT
    would be blocked at the plan as unassociated. Only ever corrects a value
    that is empty or has become invalid; a choice the operator made from this
    OLT's own list is never re-decided under them.
  */
  useEffect(() => {
    if (request.action !== 'management_mode' || request.mode !== 'tr069') return;
    if (!defaultProfile) return;
    const valid = profiles.some((p) => p.profileId === request.tr069ProfileId);
    if (!request.tr069ProfileId || !valid) {
      setRequest({ ...request, tr069ProfileId: defaultProfile.profileId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, profiles, defaultProfile?.profileId]);

  /*
    Same rule as the profile above, for the management VLAN: with no untagged
    option the select must open on a real VLAN — the chassis's own marked
    management VLAN — and follow a chassis change, since VLAN 10 from gachie1
    means nothing on a chassis that manages on 2002. Only ever corrects an
    empty or now-invalid value; a pick made from this chassis's list stands.
  */
  useEffect(() => {
    if (request.action !== 'management_mode') return;
    const first = managementVlans[0];
    if (!first) return;
    const valid = managementVlans.some((v) => v.vlanId === request.mgmtVlan);
    if (request.mgmtVlan === null || !valid) {
      setRequest({ ...request, mgmtVlan: first.vlanId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, managementVlans]);

  switch (request.action) {
    case 'device_preset':
      return (
        <Field label="Preset">
          <select
            className="input"
            value={request.presetId}
            onChange={(e) => setRequest({ ...request, presetId: e.target.value })}
          >
            <option value="">Choose a preset</option>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.target.toUpperCase()})
              </option>
            ))}
          </select>
        </Field>
      );
    case 'management_mode':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Manage over">
            <select
              className="input"
              value={request.mode}
              onChange={(e) =>
                setRequest({
                  ...request,
                  mode: e.target.value as 'tr069' | 'omci',
                  // A profile only means anything on the way TO TR-069, and the
                  // API refuses it beside `omci` rather than ignoring it — so
                  // the form drops it on the switch instead of sending a
                  // request it knows will be blocked.
                  ...(e.target.value === 'omci' ? { tr069ProfileId: null } : {}),
                })
              }
            >
              <option value="tr069">TR-069</option>
              <option value="omci">OMCI only</option>
            </select>
          </Field>
          {request.mode === 'tr069' && (
            <Field
              label="TR-069 profile"
              hint={
                oltChosen
                  ? 'Which ACS the batch is pointed at — only profiles this chassis is associated with are offered.'
                  : 'Choose a chassis first.'
              }
            >
              {/* Actual profiles by name, the chassis's default preselected —
                  never a sentence about inheritance. */}
              <select
                className="input"
                value={request.tr069ProfileId ?? defaultProfile?.profileId ?? ''}
                disabled={!oltChosen || profiles.length === 0}
                onChange={(e) => setRequest({ ...request, tr069ProfileId: e.target.value })}
              >
                {profiles.map((p) => (
                  <option key={p.profileId} value={p.profileId}>
                    {p.name}
                    {p.isDefault ? ' — this OLT\u2019s default' : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field
            label="Management VLAN"
            hint={
              !oltChosen
                ? 'Choose a chassis first.'
                : managementVlans.length === 0
                  ? 'No VLAN on this chassis is marked for management — mark one on the OLT’s VLANs tab, or run Detect there, first.'
                  : 'Only VLANs this chassis manages ONUs on. A subscriber VLAN is refused: the ONU would hold no address on it and never inform.'
            }
          >
            <select
              className="input"
              value={request.mgmtVlan ?? ''}
              onChange={(e) =>
                setRequest({ ...request, mgmtVlan: e.target.value ? Number(e.target.value) : null })
              }
              disabled={!oltChosen}
            >
              {/* Actual VLANs only, per the operator's rule — nothing on this
                  fleet manages an ONU untagged, and "Untagged" at the top of
                  the list was a wrong default one slip above the right one.
                  The API refuses TR-069 with no VLAN anyway; a form must not
                  offer what the plan refuses. */}
              {managementVlans.map((v) => (
                <option key={v.id} value={v.vlanId}>
                  {vlanLabel(v)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      );
    case 'tr069_profile':
      return (
        <Field
          label="Profile"
          hint={
            oltChosen
              ? 'The first entry follows the chassis — ONUs on it move if the chassis default is ever changed. Picking any other pins these ONUs to it by name.'
              : 'Choose a chassis first.'
          }
        >
          <select
            className="input"
            value={request.profileId ?? ''}
            disabled={!oltChosen || profiles.length === 0}
            onChange={(e) => setRequest({ ...request, profileId: e.target.value || null })}
          >
            {/* '' is still "no override" — but it wears the NAME of the profile
                it resolves to today, because "Inherit from the OLT" made an
                operator open another page to learn which ACS that meant. */}
            <option value="">
              {defaultProfile
                ? `${defaultProfile.name} — this OLT\u2019s default`
                : 'the OLT default'}
            </option>
            {profiles
              .filter((p) => !p.isDefault)
              .map((p) => (
                <option key={p.profileId} value={p.profileId}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
      );
    case 'wan_mode':
      return (
        <WanModeFields
          request={request}
          setRequest={setRequest}
          subscriberVlans={subscriberVlans}
          vlan1Withheld={vlan1Withheld}
          oltChosen={oltChosen}
          oltVendor={oltVendor}
        />
      );
    case 'attach_vlan':
    // `vlan_mode` is not offered in the picker since 2026-09-14 (`wan_mode`
    // carries everything it did; the `?action=vlan_mode` link is translated on
    // mount). Still in the request union for the ledger's runs, so it keeps a
    // form rather than falling through to nothing.
    case 'vlan_mode':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="VLAN"
            hint={
              !oltChosen
                ? 'Choose a chassis first.'
                : subscriberVlans.length === 0
                  ? 'This chassis carries no VLAN a subscriber may be put on.'
                  : vlan1Withheld
                    ? 'Only VLANs this chassis carries and offers to subscribers. The management VLAN is not one, and neither is VLAN 1 — it is off for this organization.'
                    : 'Only VLANs this chassis carries and offers to subscribers. The management VLAN is not one.'
            }
          >
            <select
              className="input"
              value={request.vlanId > 0 ? String(request.vlanId) : ''}
              onChange={(e) => setRequest({ ...request, vlanId: Number(e.target.value) || 0 })}
              disabled={!oltChosen || subscriberVlans.length === 0}
            >
              <option value="">Choose a VLAN</option>
              {subscriberVlans.map((v) => (
                <option key={v.id} value={v.vlanId}>
                  {vlanLabel(v)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Treatment" hint="Routed: the ONU terminates it. Bridged: it passes through.">
            <select
              className="input"
              value={request.mode}
              onChange={(e) =>
                setRequest({ ...request, mode: e.target.value as 'bridge' | 'route' })
              }
            >
              <option value="route">Routed by the ONU</option>
              <option value="bridge">Bridged</option>
            </select>
          </Field>
        </div>
      );
    case 'vlan_change':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Move from"
            hint={
              !oltChosen
                ? 'Choose a chassis first.'
                : 'The VLAN the subscriber is on today. An ONU that is not on it is left alone, and its row says so.'
            }
          >
            <select
              className="input"
              value={request.fromVlanId > 0 ? String(request.fromVlanId) : ''}
              onChange={(e) => setRequest({ ...request, fromVlanId: Number(e.target.value) || 0 })}
              disabled={!oltChosen || moveFromVlans.length === 0}
            >
              <option value="">Choose the old VLAN</option>
              {moveFromVlans.map((v) => (
                <option key={v.id} value={v.vlanId}>
                  {vlanLabel(v)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Move to" hint="Checked against what this chassis carries, like an attach.">
            <select
              className="input"
              value={request.toVlanId > 0 ? String(request.toVlanId) : ''}
              onChange={(e) => setRequest({ ...request, toVlanId: Number(e.target.value) || 0 })}
              disabled={!oltChosen || subscriberVlans.length === 0}
            >
              <option value="">Choose the new VLAN</option>
              {subscriberVlans
                .filter((v) => v.vlanId !== request.fromVlanId)
                .map((v) => (
                  <option key={v.id} value={v.vlanId}>
                    {vlanLabel(v)}
                  </option>
                ))}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="Treatment"
              hint="Keeping it is the honest default — a move that also flips bridged/routed changes two things and reports one."
            >
              <select
                className="input"
                value={request.mode ?? ''}
                onChange={(e) =>
                  setRequest({
                    ...request,
                    mode: (e.target.value || null) as 'bridge' | 'route' | null,
                  })
                }
              >
                <option value="">Keep whatever the old VLAN had</option>
                <option value="route">Routed by the ONU</option>
                <option value="bridge">Bridged</option>
              </select>
            </Field>
          </div>
        </div>
      );
    /**
     * The three whole-ONU actions have nothing to configure, and that is the
     * whole of their form. What distinguishes them is what has to be typed
     * before they run, not what has to be chosen before they are planned.
     */
    case 'onu_resync':
      return (
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={request.action === 'onu_resync' && request.reboot === true}
            onChange={(e) => setRequest({ action: 'onu_resync', reboot: e.target.checked })}
          />
          <span>
            Reboot each ONU first and write while it is off the PON
            <span className="block text-xs text-faint">
              For an ONT that declines a word while it is up — a Huawei ONT behind a ZTE chassis keeps its stored per-socket
              DHCP word otherwise. Each subscriber is down for a minute or two, and the run is confirmed like a reboot.
            </span>
          </span>
        </label>
      );
    case 'onu_adopt':
    case 'onu_summon':
    case 'onu_reboot':
    case 'onu_recreate':
    case 'router_clock':
      return null;
    case 'speed_rebind':
      return (
        <SpeedRebindFields
          request={request}
          setRequest={setRequest}
          speedProfiles={speedProfiles}
          loading={speedProfilesLoading}
          subscriberVlans={subscriberVlans}
          oltChosen={oltChosen}
        />
      );
    case 'wan_ipv6':
      return <WanIpv6Fields request={request} setRequest={setRequest} />;
    case 'onu_firewall':
      return (
        <div className="grid gap-3">
          <Field
            label="Firewall"
            hint="Whether each ONU answers unsolicited traffic from the WAN side"
          >
            <select
              className="input"
              value={request.enabled ? 'on' : 'off'}
              onChange={(e) => setRequest({ ...request, enabled: e.target.value === 'on' })}
            >
              <option value="on">Firewalled — ignore unsolicited traffic</option>
              <option value="off">Reachable — answer from the WAN side</option>
            </select>
          </Field>
          {/*
            The scoped ask, present and honest about being one third built.
            Offering only "the whole ONU" would leave an operator who asked for
            WAN-versus-management unable to see that the question was even
            heard — and the two that refuse say which probe is missing, on the
            row and here.
          */}
          <Field
            label="What it applies to"
            hint="Only the whole-ONU flag has a command any chassis here has answered."
          >
            <select
              className="input"
              value={request.scope}
              onChange={(e) =>
                setRequest({
                  ...request,
                  scope: e.target.value as 'all' | 'wan' | 'management',
                })
              }
            >
              <option value="all">The whole ONU — the OMCI firewall flag</option>
              <option value="wan">The WAN side only — not yet probed</option>
              <option value="management">The management path only — not yet probed</option>
            </select>
          </Field>
          {request.scope !== 'all' && (
            <p className="text-xs text-warn">
              Every ONU in this run will be <strong>refused</strong>, and nothing will be
              written. <code>firewall enable|disable</code> is the whole ONU and takes no side;
              the verb a scoped form would be built on is <code>security-mgmt</code> inside{' '}
              <code>pon-onu-mng</code>, which appears in no stored configuration of any chassis
              here — so nothing knows what it takes or whether it scopes a side at all. The probe
              that lifts this is <code>security-mgmt ?</code> on the registered test ONU. Build
              the plan anyway if you want the refusal on record; otherwise choose{' '}
              <em>The whole ONU</em>.
            </p>
          )}
          {!request.enabled && request.scope === 'all' && (
            <p className="text-xs text-warn">
              This puts every selected subscriber&rsquo;s router on the WAN, where it answers
              unsolicited traffic. That is what you want for devices you intend to reach directly,
              and not what you want for subscribers.
            </p>
          )}
          <p className="text-xs text-faint">
            A ZTE chassis offers this for every ONU on it, but the object belongs to the unit: a
            Huawei-made HG8546M answers &ldquo;unknown ME&rdquo; and is reported on its own row.
            The run is unaffected.
          </p>
        </div>
      );
    case 'ssid_set':
      return (
        <div className="grid gap-3">
          <SsidModeField request={request} setRequest={setRequest} />
          <SsidSetFields
            request={request}
            setRequest={setRequest}
            subscriberVlans={subscriberVlans}
          />
        </div>
      );
    case 'hotspot':
      return (
        <HotspotFields
          request={request}
          setRequest={setRequest}
          subscriberVlans={subscriberVlans}
          vlan1Withheld={vlan1Withheld}
          oltChosen={oltChosen}
        />
      );
    case 'ssid_remove':
      return (
        <div className="grid gap-3">
          <SsidModeField request={request} setRequest={setRequest} />
          <BandPicker bands={request.bands} onChange={(bands) => setRequest(withBands(request, bands))} />
          <SsidPortPicker
            request={request}
            setRequest={setRequest}
            hint24="WiFi 1–4 are the 2.4 GHz interfaces. WiFi 1 is the subscriber's own network."
            hint5g="WiFi 5–8 are the 5 GHz interfaces. WiFi 5 is the subscriber's own network."
            placeholder="Choose which WiFi"
          />
          <p className="text-xs text-muted">
            Each ONU's row names the network that interface is carrying today, read from the CPE
            itself — a slot holds whatever that subscriber has in it, so read the plan before
            running it.
          </p>
          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={request.delete}
              onChange={(e) => setRequest({ ...request, delete: e.target.checked })}
            />
            <span>
              Delete the network
              <span className="block text-xs text-muted">
                A Huawei router loses it for good. A router with a fixed set of eight (ZTE F670L)
                has the slot switched off and back on its factory name. Either way SimpleACS forgets
                its stored name and key, so nothing puts it back. WiFi 1 and WiFi 5 are never
                deleted.
              </span>
            </span>
          </label>
          <p className="text-xs text-muted">
            {request.delete ? (
              <>
                Unticked, this <strong>turns the network off</strong> instead: the WLAN, its name and
                its key stay on the CPE and a later run can switch it back on.
              </>
            ) : (
              <>
                This <strong>turns the network off</strong>; it does not delete it. The WLAN, its
                name and its key stay on the CPE and a later run can switch it back on — tick Delete
                to take it off for good.
              </>
            )}{' '}
            It goes through the ACS, so an ONU that has never informed is refused: no chassis in this
            fleet has a captured command for taking an SSID off over OMCI.
          </p>
        </div>
      );
    case 'zone':
      return (
        <Field label="Zone">
          <ZoneSelect
            value={request.zoneId ?? ''}
            zones={zones}
            emptyLabel="No zone"
            onChange={(next) => setRequest({ ...request, zoneId: next || null })}
          />
        </Field>
      );
    case 'firmware_campaign':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Campaign name">
            <Input
              value={request.name}
              onChange={(e) => setRequest({ ...request, name: e.target.value })}
              placeholder="HG8546M to V5R020"
            />
          </Field>
          <Field label="Image file">
            <Input
              value={request.fileName}
              onChange={(e) => setRequest({ ...request, fileName: e.target.value })}
              placeholder="hg8546m-v5r020.bin"
            />
          </Field>
          <Field label="Target version" hint="A device already on it is left out.">
            <Input
              value={request.targetVersion}
              onChange={(e) => setRequest({ ...request, targetVersion: e.target.value })}
            />
          </Field>
          <Field label="Batch size" hint="1 is the canary the engine defaults to.">
            <Input
              type="number"
              value={request.batchSize}
              onChange={(e) => setRequest({ ...request, batchSize: Number(e.target.value) })}
            />
          </Field>
        </div>
      );
  }
}

/**
 * Which radios this run addresses.
 *
 * Checkboxes rather than a "both / 2.4 / 5" dropdown, because both is the
 * ordinary answer and one is a deliberate narrowing — and because the server
 * treats the two independently: an ONU that can serve one of the two gets that
 * half written and carries the other half's refusal as a note on its row.
 */
function BandPicker({
  bands,
  onChange,
}: {
  bands: SsidBand[];
  onChange: (bands: SsidBand[]) => void;
}) {
  const toggle = (band: SsidBand) =>
    onChange(bands.includes(band) ? bands.filter((b) => b !== band) : [...bands, band]);
  return (
    <Field
      label="Bands"
      hint="Most of this fleet is 2.4 GHz only. A 5 GHz request is refused by name on a model with no 5 GHz radio — never moved to the other radio."
    >
      <div className="flex flex-wrap gap-4">
        {SSID_BANDS.map((band) => (
          <label key={band} className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={bands.includes(band)}
              onChange={() => toggle(band)}
            />
            {band === '2.4G' ? '2.4 GHz' : '5 GHz'}
          </label>
        ))}
      </div>
    </Field>
  );
}

/**
 * The rebind form — two profile pickers and the service they apply to.
 *
 * Every option in both pickers is a profile the CHOSEN CHASSIS reported, and
 * that is the whole design of this screen. There is no free-text box and no
 * kbps field, because the alternative to "pick one of these" is "type a speed",
 * and a speed nothing on the chassis carries has exactly one delivery
 * mechanism: writing a profile. On ZTE the write verb is silently
 * create-or-replace, so the profile it lands on takes every subscriber already
 * bound to it along with this run. The server refuses that path — this form
 * simply never suggests it.
 *
 * The bound count is on every option for the same reason. An operator looking
 * at `SMARTOLT-1G-DOWN · 1G · 2,120 bound` can see what editing that name would
 * have cost, which is a better argument than a warning.
 */
function SpeedRebindFields({
  request,
  setRequest,
  speedProfiles,
  loading,
  subscriberVlans,
  oltChosen,
}: {
  request: Extract<BulkActionRequest, { action: 'speed_rebind' }>;
  setRequest: (r: BulkActionRequest) => void;
  speedProfiles: SpeedProfileOption[];
  loading: boolean;
  subscriberVlans: OltVlan[];
  oltChosen: boolean;
}) {
  /**
   * A profile with no direction of its own belongs in BOTH lists.
   *
   * A Huawei traffic table and a C650 `traffic-profile` are policers, and the
   * direction comes from where they are attached — index 7 is bound inbound and
   * outbound on 1,949 management service-ports on this fleet. Filtering them to
   * one list would hide half of a Huawei chassis's profiles from the upstream
   * picker and leave an operator concluding the chassis has none.
   */
  const forDirection = (direction: 'up' | 'down') =>
    speedProfiles.filter((p) => p.bidirectional === true || p.direction === direction);

  const picker = (
    direction: 'up' | 'down',
    value: string | null,
    onChange: (name: string | null) => void,
  ) => {
    const options = forDirection(direction);
    return (
      <select
        className="input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={!oltChosen || options.length === 0}
      >
        <option value="">
          {direction === 'up' ? 'Leave the upstream as it is' : 'Leave the downstream as it is'}
        </option>
        {options.map((p) => (
          <option key={`${direction}:${p.name}`} value={p.name}>
            {speedProfileLabel(p)}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Upstream profile"
          hint={
            !oltChosen
              ? 'Choose a chassis first — profiles belong to the OLT.'
              : 'A profile this chassis already holds. Naming none leaves the upstream untouched.'
          }
        >
          {picker('up', request.upProfile, (name) => setRequest({ ...request, upProfile: name }))}
        </Field>
        <Field
          label="Downstream profile"
          hint="The same, for the other direction. One of the two is enough."
        >
          {picker('down', request.downProfile, (name) =>
            setRequest({ ...request, downProfile: name }),
          )}
        </Field>
      </div>

      <Field
        label="Which service"
        hint="A C650 and a Huawei bind rates per service-port, and an ONU carries several."
      >
        <select
          className="input"
          value={request.vlanId === null ? '' : String(request.vlanId)}
          onChange={(e) => setRequest({ ...request, vlanId: Number(e.target.value) || null })}
          disabled={!oltChosen || subscriberVlans.length === 0}
        >
          <option value="">The subscriber VLAN SimpleACS records for each ONU</option>
          {subscriberVlans.map((v) => (
            <option key={v.id} value={v.vlanId}>
              {vlanLabel(v)}
            </option>
          ))}
        </select>
      </Field>

      {oltChosen && !loading && speedProfiles.length === 0 && (
        <p className="text-xs text-warn">
          This chassis reported no bandwidth profiles. A rebind can only move a subscriber between
          profiles the OLT already holds, so there is nothing to pick — create one on the OLT’s
          Speed profiles tab first, deliberately and as its own step.
        </p>
      )}

      <p className="text-xs text-muted">
        This <strong>binds</strong> profiles the chassis already holds and never writes one. A
        speed nothing here carries is refused rather than created: the create verb is silently
        create-or-replace, so it would re-rate every subscriber already bound to whatever name it
        landed on.
      </p>
      <p className="text-xs text-warn">
        On a <strong>Huawei</strong> chassis the service-port is deleted and recreated at the same
        index — no in-place rewrite of its traffic tables has ever been captured here — and the
        subscriber has no forwarding path between the two commands. Their session drops. Every
        plan row says so, and until the operator accepts that cost the Huawei rows refuse. On ZTE
        the binding is replaced on an interface that stays up; no rebind has been run on any
        chassis in this fleet yet, so those rows refuse too and the plan is a rehearsal.
      </p>
    </div>
  );
}

/**
 * The set/add form.
 *
 * The interface pickers are the part worth reading. Leaving them on "primary"
 * writes the subscriber's OWN network — a rename and a re-key, and every
 * device in the house drops. Choosing a secondary interface adds a network
 * alongside it. Both are real requests, so neither is hidden, and the plan
 * says per ONU which of the two that ONU is actually getting by reporting what
 * is on the interface today.
 */
/**
 * WHICH WiFi, per band — one select each, the operator's own numbering.
 *
 * Shared by both halves of Add/Remove so the two read identically: "you select
 * one when adding", "you select one to remove/delete" (operator, 2026-09-24).
 * The values on the wire stay the chassis's `wifi_0/N`; the labels are the
 * numbers an operator uses.
 */
function SsidPortPicker({
  request,
  setRequest,
  hint24,
  hint5g,
  placeholder,
}: {
  request: Extract<BulkActionRequest, { action: 'ssid_set' | 'ssid_remove' }>;
  setRequest: (r: BulkActionRequest) => void;
  hint24: string;
  hint5g: string;
  /** Null's label. `ssid_set` defaults it to the primary; `ssid_remove` refuses to. */
  placeholder: string;
}) {
  const bands: Array<{ band: SsidBand; label: string; hint: string; field: 'port24' | 'port5g' }> = [
    { band: '2.4G', label: '2.4 GHz WiFi', hint: hint24, field: 'port24' },
    { band: '5G', label: '5 GHz WiFi', hint: hint5g, field: 'port5g' },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {bands.map(({ band, label, hint, field }) =>
        request.bands.includes(band) ? (
          <Field key={band} label={label} hint={hint}>
            <select
              className="input"
              value={request[field] ?? ''}
              onChange={(e) => setRequest({ ...request, [field]: e.target.value || null })}
            >
              <option value="">{placeholder}</option>
              {/*
                Only when ADDING. "Whichever is free" is the answer a hotspot
                wants — the slot differs per vendor, and an operator should not
                have to hold that per model — but it means nothing when taking
                a network off: there you are pointing at one that exists.
              */}
              {request.action === 'ssid_set' && (
                <option value={NEXT_FREE_SSID_PORT}>
                  Next free WiFi on this band — alongside the subscriber&rsquo;s own
                </option>
              )}
              {ssidPortsForBand(band).map((p) => (
                <option key={p} value={p}>
                  WiFi {ssidIndexOf(p)}
                  {p === primarySsidPort(band) ? " — the subscriber's own network" : ''}
                </option>
              ))}
            </select>
          </Field>
        ) : null,
      )}
    </div>
  );
}

function SsidSetFields({
  request,
  setRequest,
  subscriberVlans,
}: {
  request: Extract<BulkActionRequest, { action: 'ssid_set' }>;
  setRequest: (r: BulkActionRequest) => void;
  /** The chassis's VLANs a subscriber may be put on — management excluded. */
  subscriberVlans: OltVlan[];
}) {
  const security = request.security;
  return (
    <div className="grid gap-3">
      <Field
        label="SSID name"
        hint="1–32 characters. Spaces are fine — this is written on the CPE. An ONU that has never informed an ACS can only be written down the chassis, which takes a single WORD, and those rows say so by name."
      >
        <Input
          value={request.ssid}
          onChange={(e) => setRequest({ ...request, ssid: e.target.value })}
          placeholder="SIMPLUX-HOTSPOT"
        />
      </Field>

      <BandPicker bands={request.bands} onChange={(bands) => setRequest(withBands(request, bands))} />

      <SsidPortPicker
        request={request}
        setRequest={setRequest}
        hint24="Next free adds it beside the subscriber's own network: a dormant slot is switched on, or one is created where the CPE has none. WiFi 1 is the subscriber's own."
        hint5g="Next free adds it beside the subscriber's own network. Only models with a 5 GHz radio expose these at all."
        placeholder="Leave on the subscriber's own network"
      />

      {/*
        The WiFi card's own choice, in the same words (operator, 2026-09-24:
        "there should be an option for LAN / Access VLAN"). LAN is null — no
        VLAN is bound and the network joins the subscriber's own LAN. Access
        VLAN starts at 0, "not chosen yet", which the schema refuses and the
        Build button waits on, exactly as the hotspot form does: defaulting to
        the first VLAN in the list would put a public network on the
        subscriber's data VLAN for anyone who did not look.
      */}
      <Field
        label="Network"
        hint={
          request.vlanId === null
            ? "LAN: the network joins the subscriber's own LAN. No VLAN is bound."
            : 'Access VLAN: the interfaces this run writes are bound to this VLAN, bridged, down the chassis. ZTE chassis only.'
        }
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink">
            <input
              type="radio"
              checked={request.vlanId === null}
              onChange={() => setRequest({ ...request, vlanId: null })}
            />
            LAN
          </label>
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink">
            <input
              type="radio"
              checked={request.vlanId !== null}
              disabled={subscriberVlans.length === 0}
              onChange={() => setRequest({ ...request, vlanId: 0 })}
            />
            Access VLAN
          </label>
          {request.vlanId !== null && (
            <select
              className="input h-9 w-auto"
              value={String(request.vlanId)}
              onChange={(e) => setRequest({ ...request, vlanId: Number(e.target.value) })}
            >
              <option value="0" disabled>
                Choose the VLAN
              </option>
              {/* "12 - ftth hs": the id and the OLT's name for it, nothing else
                  (operator, 2026-09-24) — the same label the ONU's attach
                  picker and the OLT's VLANs tab use. */}
              {subscriberVlans.map((v) => (
                <option key={v.id} value={v.vlanId}>
                  {vlanIdAndName(v)}
                </option>
              ))}
            </select>
          )}
        </div>
      </Field>

      <Field label="Security">
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="radio"
              checked={security.kind === 'wpa'}
              onChange={() =>
                setRequest({
                  ...request,
                  security: { kind: 'wpa', mode: 'wpa-wpa2-psk', passphrase: '' },
                })
              }
            />
            WPA / WPA2-PSK with a passphrase
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="radio"
              checked={security.kind === 'open'}
              onChange={() =>
                setRequest({ ...request, security: { kind: 'open', acknowledgeOpen: false } })
              }
            />
            Open — no encryption
          </label>
        </div>
      </Field>

      {security.kind === 'wpa' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="WPA mode">
            <select
              className="input"
              value={security.mode}
              onChange={(e) =>
                setRequest({
                  ...request,
                  security: { ...security, mode: e.target.value as BulkSsidWpaMode },
                })
              }
            >
              {Object.entries(BULK_SSID_WPA_MODE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Passphrase"
            hint="8–63 printable characters, no spaces. Stored encrypted on the plan and never shown again."
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={security.passphrase}
              onChange={(e) =>
                setRequest({ ...request, security: { ...security, passphrase: e.target.value } })
              }
            />
          </Field>
        </div>
      ) : (
        <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1"
              checked={security.acknowledgeOpen}
              onChange={(e) =>
                setRequest({
                  ...request,
                  security: { kind: 'open', acknowledgeOpen: e.target.checked },
                })
              }
            />
            <span>
              This network will have <strong>no encryption</strong>. Anyone in range joins it
              without a key and can read everything unencrypted on it. Confirm that is what this run
              is for.
            </span>
          </label>
          <p className="mt-2 text-xs">
            A router that informs the ACS is written as an open network on the router itself. An ONU
            that has never informed can only be written down the chassis, where no open form has
            been captured on any ZTE model, so those rows are refused by name.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The public hotspot form.
 *
 * The VLAN picker is the `attach_vlan` picker — the same list of VLANs the
 * chosen chassis carries and offers to subscribers, never the management one
 * — because the VLAN half of a hotspot IS an attach. The two interfaces are
 * shown as fixed pills rather than pickers: a hotspot is by definition the
 * network alongside the subscriber's own, and the second slot on each radio is
 * the operator's convention for it. The plan still checks each against the
 * ONU's model and says, per row, which of the two that ONU has.
 *
 * WPA only, and no "open" radio at all. Open is expressible on the API (the
 * same union `ssid_set` takes) and refused by name on every row, so offering
 * it here would be a control that only ever produces a refusal; the note says
 * why it is missing rather than hiding that it is.
 */
function HotspotFields({
  request,
  setRequest,
  subscriberVlans,
  vlan1Withheld,
  oltChosen,
}: {
  request: Extract<BulkActionRequest, { action: 'hotspot' }>;
  setRequest: (r: BulkActionRequest) => void;
  subscriberVlans: OltVlan[];
  vlan1Withheld: boolean;
  oltChosen: boolean;
}) {
  const security = request.security?.kind === 'wpa' ? request.security : null;
  return (
    <div className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Hotspot">
          <select
            className="input"
            value={request.enabled ? 'on' : 'off'}
            onChange={(e) => {
              const enabled = e.target.value === 'on';
              setRequest({
                ...request,
                enabled,
                // An enable needs a key; a disable carries none and needs none.
                security: enabled
                  ? (security ?? { kind: 'wpa', mode: 'wpa-wpa2-psk', passphrase: '' })
                  : null,
              });
            }}
          >
            <option value="on">On — attach the VLAN and put the public SSID on both radios</option>
            <option value="off">Off — hide the public SSID on both radios</option>
          </select>
        </Field>
        <Field
          label="Hotspot VLAN"
          hint={
            !oltChosen
              ? 'Choose a chassis first.'
              : subscriberVlans.length === 0
                ? 'This chassis carries no VLAN a subscriber may be put on.'
                : vlan1Withheld
                  ? 'Bridged. Only VLANs this chassis carries and offers to subscribers — not the management VLAN, and not VLAN 1, which is off for this organization.'
                  : 'Bridged. Only VLANs this chassis carries and offers to subscribers — the management VLAN is not one.'
          }
        >
          <select
            className="input"
            value={request.vlanId > 0 ? String(request.vlanId) : ''}
            onChange={(e) => setRequest({ ...request, vlanId: Number(e.target.value) || 0 })}
            disabled={!oltChosen || subscriberVlans.length === 0}
          >
            <option value="">Choose a VLAN</option>
            {subscriberVlans.map((v) => (
              <option key={v.id} value={v.vlanId}>
                {vlanLabel(v)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field
        label="Public SSID name"
        hint={
          request.enabled
            ? 'One word, 1–32 characters — no spaces (the chassis takes a single WORD). Letters, digits and - _ . @ \' only.'
            : 'The name to hide. An interface carrying a different name is somebody else’s network and is left alone.'
        }
      >
        <Input
          value={request.ssid}
          onChange={(e) => setRequest({ ...request, ssid: e.target.value })}
          placeholder="MymangaPublicWiFi"
        />
      </Field>

      <Field
        label="Interfaces"
        hint="The second slot on each radio — the network alongside the subscriber’s own. A model without one of them gets the other and says so on its row."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="neutral">2.4 GHz · {request.port24}</Pill>
          <Pill tone="neutral">5 GHz · {request.port5g}</Pill>
        </div>
      </Field>

      {request.enabled && security && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="WPA mode">
            <select
              className="input"
              value={security.mode}
              onChange={(e) =>
                setRequest({
                  ...request,
                  security: { ...security, mode: e.target.value as BulkSsidWpaMode },
                })
              }
            >
              {Object.entries(BULK_SSID_WPA_MODE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Passphrase"
            hint="8–63 printable characters, no spaces. Stored encrypted on the plan and never shown again."
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={security.passphrase}
              onChange={(e) =>
                setRequest({ ...request, security: { ...security, passphrase: e.target.value } })
              }
            />
          </Field>
        </div>
      )}

      {request.enabled ? (
        <p className="text-xs text-muted">
          One chassis session per ONU: the bridged VLAN through the same lines{' '}
          <em>Attach a VLAN</em> sends, each interface bound to it with the{' '}
          <code>vlan port … mode hybrid def-vlan</code> line every access port gets, and the
          name, broadcast and key on both SSIDs. An <strong>open</strong> hotspot is not offered:
          no unencrypted authentication form has been captured on any ZTE chassis here (
          <code>ssid auth ?</code> on a C300 and a C650 is the probe), so the public SSID takes a
          WPA/WPA2 key.
        </p>
      ) : (
        <p className="text-xs text-warn">
          Off <strong>hides</strong> both SSIDs (<code>ssid ctrl … hide enable</code>, the only
          captured way to stop a ZTE SSID being offered) and leaves their name and key alone.
          The VLAN stays attached: its removal renders only from a chassis read and no VLAN
          removal has yet been run on any chassis here, so every row says so and carries the
          candidate lines for the first supervised test.
        </p>
      )}
    </div>
  );
}

/**
 * The WAN dialog's steps, for a run.
 *
 * The controls are in the dialog's step ORDER — VLAN → Mode → Addressing →
 * IP protocol → Firewall — each labelled with the step's own pill word, so the
 * plan reads back in the vocabulary the ONU page taught the operator. Where
 * the dialog opens on ONE ONU's current value, the form offers the word for
 * "each ONU's own" as a real option (`as recorded`, `current`), which the
 * resolver answers per ONU with exactly the dialog's default functions
 * (`packages/shared/src/wan-wizard.ts`). The routing steps leave the row when
 * Bridging is chosen; the Firewall step is absent on a Huawei chassis; both as
 * `wanWizardSteps` has it.
 *
 * No password and no address are typed here, on purpose. A PPPoE account and
 * a static address are per subscriber; the request says "as recorded on each
 * ONU" and the plan refuses BY NAME an ONU whose row records no complete
 * account or address. The one particular offered is the static rows' DNS,
 * because it is shared chassis plumbing (the content-named IP profile), not
 * subscriber state.
 */
/**
 * "IPv6 address" and "IPv6 prefix delegation" — two full-width rows, one per
 * question (operator: "split ipv6 and ipv6 into 2 rows"), exactly the ONU
 * page's WAN dialog layout.
 *
 * Its own component because two bulk actions ask it: `wan_mode`, where it is
 * one step of a whole-WAN re-state, and `wan_ipv6`, where it is the whole
 * action. One control, one set of refusal notes, one place to change them.
 */
function Ipv6ModeRows({
  group,
  modes,
  setModes,
}: {
  group: string;
  modes: { addressMode: Ipv6AddressMode; prefixMode: Ipv6PrefixMode };
  setModes: (addressMode: Ipv6AddressMode, prefixMode: Ipv6PrefixMode) => void;
}) {
  const bothNone = modes.addressMode === 'none' && modes.prefixMode === 'none';
  const staticChosen = modes.addressMode === 'static' || modes.prefixMode === 'static';
  return (
    <div className="grid gap-3">
      <div>
        <span className="label">IPv6 address</span>
        <div className="mt-1 flex flex-wrap gap-4">
          {IPV6_ADDRESS_MODES.map((m) => (
            <label key={m} className="flex items-center gap-1.5 text-sm text-ink">
              <input
                type="radio"
                name={`${group}-address`}
                checked={modes.addressMode === m}
                onChange={() => setModes(m, modes.prefixMode)}
              />
              {IPV6_ADDRESS_MODE_LABELS[m]}
              {m === 'static' && <span className="text-xs text-muted">— no CPE takes it today</span>}
            </label>
          ))}
        </div>
      </div>
      <div>
        <span className="label">IPv6 prefix delegation</span>
        <div className="mt-1 flex flex-wrap gap-4">
          {IPV6_PREFIX_MODES.map((m) => (
            <label key={m} className="flex items-center gap-1.5 text-sm text-ink">
              <input
                type="radio"
                name={`${group}-prefix`}
                checked={modes.prefixMode === m}
                onChange={() => setModes(modes.addressMode, m)}
              />
              {IPV6_PREFIX_MODE_LABELS[m]}
              {m === 'static' && <span className="text-xs text-muted">— no CPE takes it today</span>}
            </label>
          ))}
        </div>
      </div>
      {bothNone && (
        <p className="text-xs text-danger">
          Dual stack with neither an IPv6 address mode nor a prefix mode is IPv4 only — choose one, or choose IPv4 only.
        </p>
      )}
      {staticChosen && (
        <p className="text-xs text-warn">
          {modes.addressMode === 'static' ? IPV6_REFUSAL.staticAddress('unknown') : IPV6_REFUSAL.staticPrefix('unknown')}{' '}
          Every row will list IPv6 as not applied; build the plan anyway if you want that on record.
        </p>
      )}
    </div>
  );
}

/**
 * The IP protocol, on its own — the whole of `wan_ipv6`.
 *
 * No "as each ONU has it": that is what not running the action means. A
 * subscriber whose CPE has no observed word for the mode chosen is refused on
 * its own row by the same gate the WAN dialog uses, and nothing is written to
 * it — the other rows still run.
 */
function WanIpv6Fields({
  request,
  setRequest,
}: {
  request: Extract<BulkActionRequest, { action: 'wan_ipv6' }>;
  setRequest: (r: BulkActionRequest) => void;
}) {
  const dual = request.ipv6.protocol === 'dual_stack';
  const modes =
    request.ipv6.protocol === 'dual_stack'
      ? { addressMode: request.ipv6.addressMode, prefixMode: request.ipv6.prefixMode }
      : { addressMode: 'none' as Ipv6AddressMode, prefixMode: 'dhcpv6_pd' as Ipv6PrefixMode };
  return (
    <div className="grid gap-3">
      <Field
        label="IP protocol"
        hint="The IPv6 leaves and nothing else — the VLAN, the routed/bridged role, the addressing and the PPPoE account are not touched. Written over TR-069 where the CPE is linked; recorded and delivered at first link where it is not."
      >
        <select
          className="input"
          value={dual ? 'dual' : 'ipv4'}
          onChange={(e) =>
            setRequest({
              ...request,
              ipv6:
                e.target.value === 'ipv4'
                  ? { protocol: 'ipv4' }
                  : { protocol: 'dual_stack', addressMode: modes.addressMode, prefixMode: modes.prefixMode },
            })
          }
        >
          <option value="dual">Dual stack IPv4 + IPv6</option>
          <option value="ipv4">IPv4 only — switches IPv6 off on the CPE</option>
        </select>
      </Field>
      {dual && (
        <Ipv6ModeRows
          group="bulk-wan-ipv6"
          modes={modes}
          setModes={(addressMode, prefixMode) =>
            setRequest({ ...request, ipv6: { protocol: 'dual_stack', addressMode, prefixMode } })
          }
        />
      )}
    </div>
  );
}

/**
 * Add a network, or take one away — one entry in the picker, two actions on the
 * wire (`ssid_set` / `ssid_remove`).
 *
 * They are the same errand to an operator and were two lines apart in a list of
 * eighteen (operator, 2026-09-24). The bands and the chosen interfaces carry
 * across the switch — both halves ask "which WiFi" and mean the same thing by
 * it. The NAME does not: on the add half it is the name being written, and on
 * the remove half there is no name to give, because what comes off is whatever
 * that subscriber has in the slot.
 */
function SsidModeField({
  request,
  setRequest,
}: {
  request: Extract<BulkActionRequest, { action: 'ssid_set' | 'ssid_remove' }>;
  setRequest: (r: BulkActionRequest) => void;
}) {
  return (
    <Field
      label="Mode"
      hint={
        request.action === 'ssid_set'
          ? 'You give the name, and choose which WiFi it goes on.'
          : 'You choose which WiFi to clear. Each row names the network that interface is carrying today.'
      }
    >
      <select
        className="input"
        value={request.action}
        onChange={(e) => {
          const next = e.target.value as 'ssid_set' | 'ssid_remove';
          if (next === request.action) return;
          setRequest(
            next === 'ssid_remove'
              ? {
                  action: 'ssid_remove',
                  ssid: null,
                  port24: request.port24,
                  port5g: request.port5g,
                  bands: request.bands,
                  delete: false,
                }
              : {
                  action: 'ssid_set',
                  ssid: request.action === 'ssid_set' ? request.ssid : '',
                  bands: request.bands,
                  port24: request.port24,
                  port5g: request.port5g,
                  vlanId: request.action === 'ssid_set' ? request.vlanId : null,
                  security: { kind: 'wpa', mode: 'wpa-wpa2-psk', passphrase: '' },
                },
          );
        }}
      >
        <option value="ssid_set">Add or set an SSID</option>
        <option value="ssid_remove">Remove an SSID</option>
      </select>
    </Field>
  );
}

function WanModeFields({
  request,
  setRequest,
  subscriberVlans,
  vlan1Withheld,
  oltChosen,
  oltVendor,
}: {
  request: BulkWanModeRequest;
  setRequest: (r: BulkActionRequest) => void;
  subscriberVlans: OltVlan[];
  vlan1Withheld: boolean;
  oltChosen: boolean;
  oltVendor: string | null;
}) {
  const routed = request.mode === 'route';
  const isZte = oltVendor === 'zte';
  const huawei = oltVendor === 'huawei';
  const ipv6Choice: 'current' | 'ipv4' | 'dual' =
    request.ipv6 === 'current' ? 'current' : request.ipv6.protocol === 'ipv4' ? 'ipv4' : 'dual';
  const modes = ipv6ModesOf(request);
  const setModes = (addressMode: Ipv6AddressMode, prefixMode: Ipv6PrefixMode) =>
    setRequest({ ...request, ipv6: { protocol: 'dual_stack', addressMode, prefixMode } });
  // No IP profile is written from it on a Huawei chassis (no `wan-ip` line), so the box is not offered there.
  const dnsOffered =
    routed && !huawei && (request.addressing === 'static' || request.addressing === 'recorded');

  return (
    <div className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {/* ------------------------------------------------ VLAN */}
        <Field
          label="VLAN"
          hint={
            !oltChosen
              ? 'Choose a chassis first.'
              : 'As recorded: an ONU that records no subscriber VLAN, or two, is refused by name. Naming a VLAN attaches it only to an ONU with no subscriber VLAN yet; one that carries another is refused rather than given a second WAN. An “HS ” hotspot unit is refused by name for routing — its bridge is never re-roled.'
          }
        >
          <select
            className="input"
            value={request.vlanId === null ? '' : String(request.vlanId)}
            onChange={(e) => setRequest({ ...request, vlanId: e.target.value ? Number(e.target.value) : null })}
            disabled={!oltChosen}
          >
            <option value="">Each ONU’s own subscriber VLAN (as recorded)</option>
            {subscriberVlans.map((v) => (
              <option key={v.id} value={v.vlanId}>
                {vlanLabel(v)}
              </option>
            ))}
          </select>
          {vlan1Withheld && (
            <span className="mt-1 block text-xs text-faint">
              This chassis carries VLAN 1, but it is not offered to subscribers for this organization. ONUs already
              on it are unaffected — “as recorded” still reaches them.
            </span>
          )}
        </Field>

        {/* ------------------------------------------------ Mode */}
        <Field
          label="Mode"
          hint="Bridging: the VLAN is handed to whatever is plugged in. Routing: the ONU terminates the connection and serves the LAN behind it."
        >
          <select
            className="input"
            value={request.mode}
            onChange={(e) => setRequest(withWanMode(request, e.target.value as 'route' | 'bridge'))}
          >
            <option value="route">Routed by the ONU</option>
            <option value="bridge">Bridged</option>
          </select>
        </Field>
      </div>

      {/* ------------------------------------------------ Addressing (routed only) */}
      {routed && (
        <div className="grid gap-3">
          <Field
            label="Addressing"
            hint={
              huawei
                ? 'A choice that differs from a row’s record is refused by name on a Huawei chassis; “as recorded” passes it through unchanged.'
                : 'How each routed ONU gets its address.'
            }
          >
            <select
              className="input"
              value={request.addressing}
              onChange={(e) =>
                setRequest(withWanAddressing(request, e.target.value as BulkWanModeRequest['addressing']))
              }
            >
              <option value="recorded">
                As recorded on each ONU — its own DHCP / PPPoE / static; DHCP where the row is bridged
              </option>
              <option value="dhcp">DHCP</option>
              <option
                value="pppoe"
                disabled={huawei}
                title={
                  huawei
                    ? 'A Huawei chassis provisions PPPoE and static through the CPE (TR-069) — use the device page’s WAN editor.'
                    : undefined
                }
              >
                PPPoE — the account recorded on each ONU
              </option>
              <option
                value="static"
                disabled={huawei}
                title={
                  huawei
                    ? 'A Huawei chassis provisions PPPoE and static through the CPE (TR-069) — use the device page’s WAN editor.'
                    : undefined
                }
              >
                Static — the address recorded on each ONU
              </option>
            </select>
          </Field>
          <p className="text-xs text-muted">
            No password or address is typed here. A PPPoE account and a static address are per subscriber: each
            ONU&rsquo;s own record is what is sent (the stored password included, masked in every transcript), and on
            a ZTE chassis an ONU whose row records no complete account or address is refused by name. A Huawei
            chassis sends nothing of it — the row is passed through as it is, and a half account is said on the row.
            Set those on the ONU&rsquo;s WAN dialog first.
          </p>
          {dnsOffered && (
            <Field
              label="Primary DNS for static rows — optional"
              hint="Shared chassis plumbing: gateway and DNS become the ONU’s IP profile; the row records no DNS."
            >
              <Input
                value={request.staticDns ?? ''}
                onChange={(e) => setRequest({ ...request, staticDns: e.target.value === '' ? null : e.target.value })}
                placeholder="8.8.8.8"
              />
            </Field>
          )}
        </div>
      )}

      {/* ------------------------------------------------ IP protocol (routed only) */}
      {routed && (
        <div className="grid gap-3">
          <Field
            label="IP protocol"
            hint="Judged per ONU by the same gate as the WAN dialog: a mode a CPE has no observed word for is listed on that row as not applied, with the sentence, and the VLAN, mode and firewall still land. Delivered over TR-069 where the CPE is linked; recorded and delivered at first link where it is not."
          >
            <select
              className="input"
              value={ipv6Choice}
              onChange={(e) => {
                const value = e.target.value as 'current' | 'ipv4' | 'dual';
                setRequest({
                  ...request,
                  ipv6:
                    value === 'current'
                      ? 'current'
                      : value === 'ipv4'
                        ? { protocol: 'ipv4' }
                        : { protocol: 'dual_stack', addressMode: modes.addressMode, prefixMode: modes.prefixMode },
                });
              }}
            >
              <option value="current">
                As each ONU has it — its record, else what its CPE reads, else the firmware default (dual stack, Auto +
                DHCPv6-PD)
              </option>
              <option value="ipv4">IPv4 only — switches IPv6 off on the CPE</option>
              <option value="dual">Dual stack IPv4 + IPv6</option>
            </select>
          </Field>
          {ipv6Choice === 'dual' && (
            // Two FULL-WIDTH rows, one per question (operator: "split ipv6 and
            // ipv6 into 2 rows") — exactly the dialog's layout.
            <Ipv6ModeRows group="bulk-ipv6" modes={modes} setModes={setModes} />
          )}
        </div>
      )}

      {/* ------------------------------------------------ Firewall (ZTE chassis, routed only — a bridge has none, 2026-10-14) */}
      {isZte && routed && (
        <div className="grid gap-3">
          <Field
            label="Firewall"
            hint="Whether each ONU answers unsolicited traffic from the WAN side. Every value is written — there is no “leave as it is”."
          >
            <select
              className="input"
              value={request.firewall}
              onChange={(e) => setRequest({ ...request, firewall: e.target.value as BulkWanModeRequest['firewall'] })}
            >
              <option value="current">
                As each ONU has it — what its CPE reports over TR-069, else what the platform records, else on
                (said on the row)
              </option>
              <option value="on">Firewalled — ignore unsolicited traffic</option>
              <option value="off">Reachable — answer from the WAN side</option>
            </select>
          </Field>
          {request.firewall === 'off' && (
            <p className="text-xs text-warn">
              This puts every selected subscriber&rsquo;s router on the WAN, where it answers unsolicited traffic.
              That is what you want for devices you intend to reach directly, and not what you want for subscribers.
            </p>
          )}
          <p className="text-xs text-faint">
            Written in the same transcript as the WAN, or on the CPE over TR-069 where the WAN goes that way. A
            bridged run writes no firewall: a bridge has none of its own. A model without the OMCI firewall
            object (the HG8546M — 2,165 here) answers &ldquo;unknown ME&rdquo; on that line alone; the WAN still lands
            and the row says so.
          </p>
        </div>
      )}

      <p className="text-xs text-muted">
        The change is written to the chassis — the subscriber drops for a moment while the ONU re-provisions. Build
        the plan to read, per ONU, exactly what is sent.
      </p>
    </div>
  );
}

/**
 * How far a run has got, while it is still going.
 *
 * The numbers are counted from the target rows the executor has already
 * written, so this is a report rather than an estimate — and it is the only
 * thing on the screen that can tell a ten-minute chassis-wide run apart from a
 * stalled one. Failures are shown as they happen, not gathered up at the end:
 * an operator watching the failed count climb can stop the change to the rest
 * of the fleet, which is the whole reason to show progress at all.
 */
function RunProgress({
  progress,
  eligible,
}: {
  progress: {
    done: number;
    total: number;
    ok: number;
    failed: number;
    skipped: number;
    pct: number;
  } | null;
  eligible: number;
}) {
  // Until the first poll answers there is no honest number to show, so the bar
  // says so rather than sitting at a made-up 0%.
  const pct = progress?.pct ?? 0;
  const done = progress?.done ?? 0;
  const total = progress?.total ?? eligible;

  return (
    <div className="grid gap-2 rounded-lg border border-line bg-surface-2 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">
          {progress ? (
            <>
              {done} of {total} ONU{total === 1 ? '' : 's'}
            </>
          ) : (
            'Starting…'
          )}
        </span>
        <span className="text-sm tabular-nums text-muted">{progress ? `${pct}%` : ''}</span>
      </div>

      <div
        className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Bulk run progress"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs tabular-nums">
        <span className="text-ok">{progress?.ok ?? 0} applied</span>
        <span className={progress?.failed ? 'text-danger' : 'text-faint'}>
          {progress?.failed ?? 0} failed
        </span>
        {(progress?.skipped ?? 0) > 0 && <span className="text-faint">{progress?.skipped} skipped</span>}
        <span className="text-faint">
          Each ONU is written and read back, so this moves at the chassis&rsquo;s pace. The run is
          server-side — you can leave this page and it carries on.
        </span>
      </div>
    </div>
  );
}

/**
 * The confirmation, as a deliberate second click rather than three things to
 * type.
 *
 * It repeats the three facts the typed boxes used to extract one keystroke at a
 * time — how many subscribers, which chassis, and what it costs them — because
 * those were always the point; typing them was only ever the delivery
 * mechanism. Cancel is the default focus and Escape closes, so the safe
 * outcome is the one that happens by reflex.
 */
function ConfirmRunDialog({
  plan,
  needed,
  oltName,
  pending,
  onCancel,
  onConfirm,
}: {
  plan: BulkPlan;
  needed: { risk: BulkActionRisk; wholeChassis: boolean; oltName: boolean; phrase: string | null };
  oltName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const tone =
    needed.risk === 'rebuild'
      ? 'border-danger/30 bg-danger-soft text-danger'
      : needed.risk === 'service'
        ? 'border-warn/30 bg-warn-soft text-warn'
        : 'border-line bg-surface-2 text-muted';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Confirm this bulk run"
        className="relative z-10 w-full max-w-lg rounded-xl border border-line bg-surface p-5 shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold">Apply to {plan.eligibleCount} ONU{plan.eligibleCount === 1 ? '' : 's'}?</h2>
          <Button size="sm" variant="ghost" icon={<X className="size-4" />} onClick={onCancel}>
            <span className="sr-only">Cancel</span>
          </Button>
        </div>

        <dl className="mt-4 grid gap-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Subscribers affected</dt>
            <dd className="font-medium tabular-nums">{plan.eligibleCount}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Chassis</dt>
            <dd className="font-medium">{oltName}</dd>
          </div>
          {needed.wholeChassis && (
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Coverage</dt>
              <dd className="font-medium">Every ONU on this chassis — nothing filtered out</dd>
            </div>
          )}
        </dl>

        <div className={`mt-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${tone}`}>
          <ShieldAlert className="mt-0.5 size-4 shrink-0" />
          <span>{RISK_NOTE[needed.risk].what}</span>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          {/* Cancel first and autofocused: the reflex outcome is the safe one. */}
          <Button variant="ghost" onClick={onCancel} autoFocus>
            Cancel
          </Button>
          <Button variant="danger" icon={<Play className="size-4" />} loading={pending} onClick={onConfirm}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
